import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import './HealthAssessment.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faDownload, faChevronLeft, faChevronRight, faClipboardCheck, faPlayCircle, faFileMedical, faListCheck, faSmile, faIdCard, faVirus, faHistory, faBed, faUtensils, faWineGlass, faSmoking, faDna, faToilet, faRunning, faBrain, faBriefcase, faChartLine, faArrowUp, faArrowDown, faEye } from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import healthAssessmentService, { HealthAssessmentListItem, ReportData } from '../../services/healthAssessmentService';
import BackButton from '../../components/BackButton/BackButton';
import HealthAssessmentReport from './HealthAssessmentReport';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { Dependant } from '../../types';

// Step configuration for progress tracking
const ASSESSMENT_STEPS = [
  { key: 'records', label: 'Records', icon: faClipboardCheck },
  { key: 'moodAssessment', label: 'Mood', icon: faSmile },
  { key: 'basicProfile', label: 'Basic Profile', icon: faIdCard },
  { key: 'presentingIllness', label: 'Illness', icon: faVirus },
  { key: 'pastHistory', label: 'Past History', icon: faHistory },
  { key: 'sleepAssessment', label: 'Sleep', icon: faBed },
  { key: 'eatingHabits', label: 'Eating', icon: faUtensils },
  { key: 'drinkingHabits', label: 'Drinking', icon: faWineGlass },
  { key: 'smokingHabits', label: 'Smoking', icon: faSmoking },
  { key: 'hereditaryQuestions', label: 'Hereditary', icon: faDna },
  { key: 'bowelBladderHabits', label: 'Bowel/Bladder', icon: faToilet },
  { key: 'fitnessProfile', label: 'Fitness', icon: faRunning },
  { key: 'mentalWellness', label: 'Mental Health', icon: faBrain },
  { key: 'employeeWellness', label: 'Wellness', icon: faBriefcase },
];

// Step section header configs: color theme per step
const STEP_THEMES: Record<string, { headerClass: string; cardClass: string; iconBg: string; iconColor: string }> = {
  records: { headerClass: 'ha-form-section-header--teal', cardClass: '', iconBg: 'rgba(6, 182, 212, 0.1)', iconColor: '#06b6d4' },
  moodAssessment: { headerClass: 'ha-form-section-header--amber', cardClass: 'ha-form-card--amber', iconBg: 'rgba(245, 158, 11, 0.1)', iconColor: '#f59e0b' },
  basicProfile: { headerClass: 'ha-form-section-header--blue', cardClass: 'ha-form-card--blue', iconBg: 'rgba(59, 130, 246, 0.1)', iconColor: '#3b82f6' },
  presentingIllness: { headerClass: 'ha-form-section-header--rose', cardClass: 'ha-form-card--rose', iconBg: 'rgba(244, 63, 94, 0.1)', iconColor: '#f43f5e' },
  pastHistory: { headerClass: 'ha-form-section-header--indigo', cardClass: 'ha-form-card--indigo', iconBg: 'rgba(99, 102, 241, 0.1)', iconColor: '#6366f1' },
  sleepAssessment: { headerClass: 'ha-form-section-header--purple', cardClass: 'ha-form-card--purple', iconBg: 'rgba(139, 92, 246, 0.1)', iconColor: '#8b5cf6' },
  eatingHabits: { headerClass: 'ha-form-section-header--green', cardClass: 'ha-form-card--green', iconBg: 'rgba(16, 185, 129, 0.1)', iconColor: '#10b981' },
  drinkingHabits: { headerClass: 'ha-form-section-header--orange', cardClass: 'ha-form-card--orange', iconBg: 'rgba(249, 115, 22, 0.1)', iconColor: '#f97316' },
  smokingHabits: { headerClass: 'ha-form-section-header--rose', cardClass: 'ha-form-card--rose', iconBg: 'rgba(244, 63, 94, 0.1)', iconColor: '#f43f5e' },
  hereditaryQuestions: { headerClass: 'ha-form-section-header--indigo', cardClass: 'ha-form-card--indigo', iconBg: 'rgba(99, 102, 241, 0.1)', iconColor: '#6366f1' },
  bowelBladderHabits: { headerClass: 'ha-form-section-header--amber', cardClass: 'ha-form-card--amber', iconBg: 'rgba(245, 158, 11, 0.1)', iconColor: '#f59e0b' },
  fitnessProfile: { headerClass: 'ha-form-section-header--green', cardClass: 'ha-form-card--green', iconBg: 'rgba(16, 185, 129, 0.1)', iconColor: '#10b981' },
  mentalWellness: { headerClass: 'ha-form-section-header--blue', cardClass: 'ha-form-card--blue', iconBg: 'rgba(59, 130, 246, 0.1)', iconColor: '#3b82f6' },
  employeeWellness: { headerClass: 'ha-form-section-header--teal', cardClass: '', iconBg: 'rgba(6, 182, 212, 0.1)', iconColor: '#06b6d4' },
};

const StepProgressBar: React.FC<{ currentStep: string; onConfirmExit?: () => void }> = ({ currentStep, onConfirmExit }) => {
  const [showExit, setShowExit] = useState(false);
  const currentIndex = ASSESSMENT_STEPS.findIndex(s => s.key === currentStep);
  // Skip showing for records (pre-assessment step)
  const formStepIndex = currentIndex - 1; // offset by records
  const totalFormSteps = ASSESSMENT_STEPS.length - 1;
  const progressPercent = formStepIndex >= 0 ? Math.round(((formStepIndex + 1) / totalFormSteps) * 100) : 0;

  if (currentIndex < 1) return null; // Don't show for records

  return (
    <>
      <div className="ha-step-progress-wrapper">
        <div className="ha-step-progress">
          <div className="ha-step-progress-header">
            <span className="ha-step-progress-label">Assessment Progress</span>
            <div className="ha-step-progress-actions">
              <span className="ha-step-progress-count">Step {formStepIndex + 1} of {totalFormSteps}</span>
              {onConfirmExit && (
                <button
                  type="button"
                  className="ha-exit-btn"
                  onClick={() => setShowExit(true)}
                  title="Save progress and return to Health Assessment home"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  <span>Save &amp; Exit</span>
                </button>
              )}
            </div>
          </div>
          <div className="ha-step-progress-bar">
            <div className="ha-step-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="ha-step-dots">
            {ASSESSMENT_STEPS.slice(1).map((step, idx) => (
              <span
                key={step.key}
                className={`ha-step-dot${idx < formStepIndex ? ' completed' : ''}${idx === formStepIndex ? ' active' : ''}`}
                title={step.label}
              />
            ))}
          </div>
        </div>
      </div>

      {showExit && onConfirmExit && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="popup-close" onClick={() => setShowExit(false)} aria-label="Close">×</button>
            <h3>Save progress and exit assessment?</h3>
            <p style={{ marginTop: -16, marginBottom: 24, color: '#64748b', fontSize: '0.9rem' }}>
              Your answers up to this point are already saved. You can resume later from the records page.
            </p>
            <div className="popup-actions">
              <button
                className="popup-btn"
                onClick={() => { setShowExit(false); onConfirmExit(); }}
              >
                Yes, Exit
              </button>
              <button
                className="popup-btn"
                style={{ background: '#e2e8f0', color: '#334155' }}
                onClick={() => setShowExit(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};



// const SummaryPage: React.FC<{
//   data: any;
//   onBackToStart: () => void;
// }> = ({ data, onBackToStart }) => {

//   return (
//     <div className="summary-page">
//       <h2>Assessment Summary</h2>
//       <p>Thank you for completing the assessment. Here is a summary of your responses:</p>

//       <div className="summary-section">
//         <h3>General Details</h3>
//         <p>Mood: {data.generalDetails?.mood}</p>
//         {/* Add more fields */}
//       </div>

//       <div className="summary-section">
//         <h3>Past History</h3>
//         <p>Chronic Illness: {data.pastHistory?.chronicIllness}</p>
//         <p>Surgery History: {data.pastHistory?.surgeryHistory}</p>
//       </div>

//       <button className="action-btn" onClick={onBackToStart}>
//         Start New Assessment
//       </button>
//     </div>
//   );
// };

// Frequency scale for mental-wellness questions — values must match the
// backend FREQ5_CHOICES (never / occasionally / often / very_often / always).
const FREQ5_OPTIONS: { value: string; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: 'occasionally', label: 'Occasionally' },
  { value: 'often', label: 'Often' },
  { value: 'very_often', label: 'Very often' },
  { value: 'always', label: 'Always' },
];

const HealthAssessment: React.FC = () => {
  const { t } = useTranslation(['health', 'common']);
  const { activeProfile } = useProfile();
  const { canAccess } = useServiceAccess();
  const activeProfileKey = activeProfile
    ? `${activeProfile.type}:${activeProfile.id}`
    : 'none';
  const activeProfileKeyRef = useRef(activeProfileKey);
  activeProfileKeyRef.current = activeProfileKey;
  const [view, setView] = useState('initial');
  const navigate = useNavigate();
  const [age, setAge] = useState<number>(0);
  const [gender, setGender] = useState<string>('');

  // API State
  const [assessmentRecords, setAssessmentRecords] = useState<HealthAssessmentListItem[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [currentAssessmentId, setCurrentAssessmentId] = useState<number | null>(null);
  const [savingProgress, setSavingProgress] = useState(false);
  const [submittedReportData, setSubmittedReportData] = useState<ReportData | null>(null);
  const [diseasePrediction, setDiseasePrediction] = useState<any>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  // In-app toast notification (replaces browser alert)
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'error') => {
    setToastMsg({ message, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  const [showNewAssessmentConfirm, setShowNewAssessmentConfirm] = useState(false);
  const [pendingAssessmentCount, setPendingAssessmentCount] = useState(0);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const goToView = (nextView: string) => {
    setValidationError('');
    setView(nextView);
  };

  const handleExitAssessment = () => {
    setCurrentAssessmentId(null);
    goToView('initial');
  };

  // Confetti burst on submission
  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#f43f5e', '#fbbf24', '#a78bfa', '#34d399'];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    // Initial big burst from center
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors,
    });

    // Continuous side cannons
    frame();
  }, []);

  useEffect(() => {
    if (view === 'submitted') {
      fireConfetti();
    }
  }, [view, fireConfetti]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [view]);

  // Fetch assessments from API
  const fetchAssessments = async () => {
    const requestProfileKey = activeProfileKey;
    try {
      setLoadingRecords(true);
      const data = await healthAssessmentService.getAssessments();
      if (activeProfileKeyRef.current === requestProfileKey) {
        setAssessmentRecords(data);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      if (activeProfileKeyRef.current === requestProfileKey) {
        setLoadingRecords(false);
      }
    }
  };

  // Create new assessment
  const createNewAssessment = async (forWhom: 'self' | 'dependant', dependantId?: number) => {
    const requestProfileKey = activeProfileKey;
    try {
      setSavingProgress(true);
      const assessment = await healthAssessmentService.createAssessment({
        for_whom: forWhom,
        dependant: dependantId
      });
      if (activeProfileKeyRef.current !== requestProfileKey) return null;
      setCurrentAssessmentId(assessment.id);
      return assessment;
    } catch (error) {
      console.error('Error creating assessment:', error);
      if (activeProfileKeyRef.current === requestProfileKey) {
        showToast('Failed to create assessment. Please try again.');
      }
      return null;
    } finally {
      if (activeProfileKeyRef.current === requestProfileKey) {
        setSavingProgress(false);
      }
    }
  };

  // Save current step progress
  const saveStepProgress = async (
    stepData: Record<string, any>,
    assessmentId: number | null = currentAssessmentId
  ): Promise<boolean> => {
    if (!assessmentId) return false;
    const requestProfileKey = activeProfileKey;
    try {
      setSavingProgress(true);
      await healthAssessmentService.updateAssessment(assessmentId, stepData);
      return activeProfileKeyRef.current === requestProfileKey;
    } catch (error) {
      console.error('Error saving progress:', error);
      if (activeProfileKeyRef.current === requestProfileKey) {
        showToast('Failed to save progress. Please try again.');
      }
      return false;
    } finally {
      if (activeProfileKeyRef.current === requestProfileKey) {
        setSavingProgress(false);
      }
    }
  };

  // Submit final assessment
  const submitAssessment = async (): Promise<boolean> => {
    if (!currentAssessmentId) return false;
    const requestProfileKey = activeProfileKey;
    try {
      setSavingProgress(true);
      const result = await healthAssessmentService.submitAssessment(currentAssessmentId);
      if (activeProfileKeyRef.current !== requestProfileKey) return false;
      setSubmittedReportData(result.report_data ?? null);
      setView('submitted');
      fetchAssessments();
      return true;
    } catch (error) {
      console.error('Error submitting assessment:', error);
      if (activeProfileKeyRef.current === requestProfileKey) {
        showToast('Failed to submit assessment. Please try again.');
      }
      return false;
    } finally {
      if (activeProfileKeyRef.current === requestProfileKey) {
        setSavingProgress(false);
      }
    }
  };

  // Download report
  const downloadReport = async (id: number) => {
    try {
      const blob = await healthAssessmentService.downloadReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-assessment-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
      showToast('Failed to download report. Please try again.');
    }
  };

  // Load assessment data into form state for resume
  const loadAssessmentIntoState = async (id: number) => {
    const requestProfileKey = activeProfileKey;
    try {
      const data = await healthAssessmentService.getAssessment(id);
      if (activeProfileKeyRef.current !== requestProfileKey) return null;
      // Mood
      if (data.mood_today != null) setMoodValue(data.mood_today - 1);
      // Basic Profile
      if (data.height_unit) setHeightUnit(data.height_unit);
      if (data.height_feet != null) setHeightFeet(String(data.height_feet));
      if (data.height_inches != null) setHeightInches(String(data.height_inches));
      if (data.height_cm != null) setHeightCm(String(data.height_cm));
      if (data.weight_kg != null) setWeightValue(String(data.weight_kg));
      if (data.health_opinion) setHealthOpinion(data.health_opinion as any);
      // Presenting Illness
      if (data.presenting_illness) {
        setSelectedIllnesses(
          Array.isArray(data.presenting_illness)
            ? data.presenting_illness
            : typeof data.presenting_illness === 'string' && data.presenting_illness.includes(',')
              ? data.presenting_illness.split(',').map((s: string) => s.trim())
              : [data.presenting_illness]
        );
      }
      if (data.presenting_illness_other) setPresentingIllnessOther(data.presenting_illness_other);
      // Past History
      if (data.chronic_illness != null) setHasChronicIllness(data.chronic_illness ? 'yes' : 'no');
      if (data.chronic_illness_details) setChronicIllnessDetail(data.chronic_illness_details);
      if (data.surgery_history != null) setHasSurgery(data.surgery_history ? 'yes' : 'no');
      if (data.surgery_history_details) setSurgeryDetail(data.surgery_history_details);
      // Sleep
      if (data.sleep_hours) setSleepHours(data.sleep_hours as any);
      if (data.wakeup_midnight != null) setWakeUpMidSleep(data.wakeup_midnight ? 'yes' : 'no');
      if (data.wakeup_midnight_reasons) setWakeUpReason(data.wakeup_midnight_reasons);
      if (data.tired_morning != null) setMorningFeeling(data.tired_morning ? 'yes' : 'no');
      // Eating Habits
      if (data.is_veg != null) setIsNonVeg(!data.is_veg);
      if (data.junk_food_freq) setFriedJunkFood(data.junk_food_freq);
      if (data.milk_dairy_freq) setMilkDairy(data.milk_dairy_freq);
      if (data.fruits_veg_freq) setFreshFruitsVeg(data.fruits_veg_freq);
      if (data.water_intake != null) setWaterIntake(String(data.water_intake));
      if (data.non_veg_freq) setMeatSeafood(data.non_veg_freq);
      // Alcohol
      if (data.alcohol_current != null) setConsumingAlcohol(data.alcohol_current ? 'yes' : 'no');
      if (data.alcohol_duration) setDrinkingDuration(data.alcohol_duration);
      if (data.alcohol_frequency) setDrinkingFrequency(data.alcohol_frequency);
      if (data.alcohol_planning_quit != null) setTriedQuitDrinking(data.alcohol_planning_quit ? 'yes' : 'no');
      if (data.alcohol_quantity) setAlcoholQuantity(data.alcohol_quantity);
      if (data.alcohol_past != null) setConsumedInPast(data.alcohol_past ? 'yes' : 'no');
      if (data.alcohol_quit_period) setQuitDrinkingWhen(data.alcohol_quit_period);
      // Tobacco
      if (data.tobacco_current != null) setConsumingTobacco(data.tobacco_current ? 'yes' : 'no');
      if (data.tobacco_type) setTobaccoType(data.tobacco_type);
      if (data.tobacco_duration) setSmokingDuration(data.tobacco_duration);
      if (data.tobacco_planning_quit != null) setTriedQuitSmoking(data.tobacco_planning_quit ? 'yes' : 'no');
      if (data.tobacco_quit != null) setQuitHabit(data.tobacco_quit ? 'yes' : 'no');
      if (data.tobacco_quit_period) setQuitSmokingWhen(data.tobacco_quit_period);
      // Hereditary
      if (data.family_chronic_illness != null) setFamilyIllness(data.family_chronic_illness ? 'yes' : 'no');
      if (data.family_illness_records && Array.isArray(data.family_illness_records)) {
        const depMap: Record<number, string> = {};
        data.family_illness_records.forEach((item: any) => {
          if (typeof item === 'object' && item.dependant != null) {
            depMap[item.dependant] = item.disease || '';
          } else if (typeof item === 'number') {
            depMap[item] = '';
          }
        });
        setSelectedFamilyDependants(depMap);
      }
      if (data.checkup_frequency) setPhysicianCheckup(data.checkup_frequency);
      if (data.taking_regular_meds != null) setCurrentlyTakingMedication(data.taking_regular_meds ? 'yes' : 'no');
      if (data.stopped_meds_without_doctor != null) setStoppedMedication(data.stopped_meds_without_doctor ? 'yes' : 'no');
      if (data.other_alt_medicine != null) setOtherMedicineSources(data.other_alt_medicine ? 'yes' : 'no');
      // Bowel/Bladder
      if (data.difficulty_urine != null) setDifficultyUrinating(data.difficulty_urine ? 'yes' : 'no');
      if (data.difficulty_urine_reasons) setUrineDifficultyType(data.difficulty_urine_reasons);
      if (data.difficulty_stools != null) setDifficultyStooling(data.difficulty_stools ? 'yes' : 'no');
      // Fitness
      if (data.stretch_duration) setStretching(data.stretch_duration);
      if (data.cardio_duration) setAerobicActivity(data.cardio_duration);
      if (data.strength_duration) setStrengthTraining(data.strength_duration);
      if (data.walking_duration) setWalking(data.walking_duration);
      if (data.other_activity) setOtherActivity(data.other_activity);
      if (data.other_activity_text) setOtherActivityText(data.other_activity_text);
      // Mental (frequency scale)
      if (data.low_interest != null) setLittleInterest(data.low_interest || '');
      if (data.depressed != null) setFeelingSad(data.depressed || '');
      if (data.sleep_appetite_issue != null) setSleepAppetiteProblems(data.sleep_appetite_issue || '');
      if (data.low_energy != null) setLowEnergy(data.low_energy || '');
      if (data.anxious != null) setAnxiousRestless(data.anxious || '');
      // Employee Wellness
      if (data.work_stress_affecting_life != null) setWorkStress(data.work_stress_affecting_life ? 'yes' : 'no');
      if (data.work_stress_reasons) {
        setStressReasons(
          Array.isArray(data.work_stress_reasons)
            ? data.work_stress_reasons
            : data.work_stress_reasons.split(', ')
        );
      }
      // Name
      if (data.for_whom === 'self') {
        setSelectedUserName(data.dependant_data?.name || 'Self');
      } else if (data.dependant_data?.name) {
        setSelectedUserName(data.dependant_data.name);
      }
      return data;
    } catch (error) {
      console.error('Error loading assessment data:', error);
      return null;
    }
  };

  // Resume assessment - load data and go to correct step
  const resumeAssessment = async (record: HealthAssessmentListItem) => {
    const requestProfileKey = activeProfileKey;
    setCurrentAssessmentId(record.id);
    const data = await loadAssessmentIntoState(record.id);
    if (!data || activeProfileKeyRef.current !== requestProfileKey) return;
    // Map current_step (1-13) to the view key for that wizard step.
    const stepIndex = record.current_step;
    const stepMap: Record<number, string> = {
      1: 'moodAssessment', 2: 'basicProfile', 3: 'presentingIllness', 4: 'pastHistory',
      5: 'sleepAssessment', 6: 'eatingHabits', 7: 'drinkingHabits', 8: 'smokingHabits',
      9: 'hereditaryQuestions', 10: 'bowelBladderHabits', 11: 'fitnessProfile', 12: 'mentalWellness', 13: 'employeeWellness',
    };
    setView(stepMap[stepIndex] || 'moodAssessment');
  };

  // View completed assessment report
  const viewAssessmentReport = async (record: HealthAssessmentListItem) => {
    const requestProfileKey = activeProfileKey;
    setCurrentAssessmentId(record.id);
    setSubmittedReportData(null); // force /report/ fetch inside the report component
    const data = await healthAssessmentService.getAssessment(record.id);
    if (activeProfileKeyRef.current !== requestProfileKey) return;
    // Fetch gender/age from prefill
    try {
      // Only pass dependant_id for dependant records, not for self
      const isSelf = data.for_whom === 'self';
      const depId = !isSelf && data.dependant ? data.dependant : undefined;
      const prefill = await healthAssessmentService.getPrefill(depId);
      if (activeProfileKeyRef.current !== requestProfileKey) return;
      if (prefill.gender) setGender(prefill.gender);
      if (prefill.age) setAge(prefill.age);
    } catch (e) {
      console.error('Error fetching prefill for report:', e);
    }
    if (activeProfileKeyRef.current !== requestProfileKey) return;
    if (record.for_whom && typeof record.for_whom === 'object') {
      setSelectedUserName(record.for_whom.name || 'Self');
    } else if (record.dependant_data?.name) {
      setSelectedUserName(record.dependant_data.name);
    } else {
      setSelectedUserName('Self');
    }
    setView('viewReport');
  };

  // Load assessments on initial and records views
  useEffect(() => {
    if (view === 'initial' || view === 'records') {
      fetchAssessments();
    }
  }, [view, activeProfile]);

  // Fetch disease prediction when assessment is submitted
  useEffect(() => {
    if (view === 'submitted' || view === 'viewReport') {
      if (!canAccess('disease-prediction')) return;
      const fetchPrediction = async () => {
        try {
          const res = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.latest);
          if (res.data.has_prediction) setDiseasePrediction(res.data);
        } catch { /* silently fail */ }
      };
      fetchPrediction();
    }
  }, [view, canAccess]);

  const [hraChoices, setHraChoices] = useState<{
    work_stress_reasons?: Record<string, string>;
    family_disease_list?: Record<string, string>;
    fitness_duration?: Record<string, string>;
    mood_today?: Record<string, string>;
    height_unit?: Record<string, string>;
    bmi_category?: Record<string, string>;
    eat_frequency?: Record<string, string>;
    sleep_hours?: Record<string, string>;
    checkup_frequency?: Record<string, string>;
    other_activity?: Record<string, string>;
    alcohol_frequency?: Record<string, string>;
    alcohol_duration?: Record<string, string>;
    tobacco_type?: Record<string, string>;
    wakeup_midnight_reasons?: Record<string, string>;
    urine_difficulty_reasons?: Record<string, string>;
  }>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.healthAssessment.choices);
        if (!cancelled && res.data && typeof res.data === 'object') {
          setHraChoices(res.data);
        }
      } catch { /* keep the fallback hardcoded labels */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [moodValue, setMoodValue] = useState(2);
  // Height State
const [heightUnit, setHeightUnit] = useState<'feet' | 'cm'>('feet');
const [heightFeet, setHeightFeet] = useState('');
const [heightInches, setHeightInches] = useState('');
const [heightCm, setHeightCm] = useState('');

// Weight State (kg only)
const [weightValue, setWeightValue] = useState('');

// BMI Calculation (optional default)
const [bmi, setBmi] = useState('');
const [bmiStatus, setBmiStatus] = useState('');


// Health Opinion
const [healthOpinion, setHealthOpinion] = useState<'extremely_healthy' | 'healthy' | 'unhealthy' | ''>('');
const [showUnhealthyPopup, setShowUnhealthyPopup] = useState(false);

const [selectedIllnesses, setSelectedIllnesses] = useState<string[]>([]);
const [presentingIllnessOther, setPresentingIllnessOther] = useState<string>('');


const [hasChronicIllness, setHasChronicIllness] = useState<string>('');
const [hasSurgery, setHasSurgery] = useState<string>('');


const [sleepHours, setSleepHours] = useState<'lt_7' | 'gt_7' | ''>('');
const [wakeUpMidSleep, setWakeUpMidSleep] = useState<'yes' | 'no' | ''>('');
const [morningFeeling, setMorningFeeling] = useState<'yes' | 'no' | ''>('');

const [isNonVeg, setIsNonVeg] = useState<boolean>(false);

const [friedJunkFood, setFriedJunkFood] = useState<string>('');
const [milkDairy, setMilkDairy] = useState<string>('');
const [freshFruitsVeg, setFreshFruitsVeg] = useState<string>('');
const [waterIntake, setWaterIntake] = useState<string>('');
const [meatSeafood, setMeatSeafood] = useState<string>('');

const [consumingAlcohol, setConsumingAlcohol] = useState<string>('');
const [consumedInPast, setConsumedInPast] = useState<string>('');

const [consumingTobacco, setConsumingTobacco] = useState<string>('');
const [quitHabit, setQuitHabit] = useState<string>('');

const [familyIllness, setFamilyIllness] = useState<string>('');
const [familyDependants, setFamilyDependants] = useState<Dependant[]>([]);
const [selectedFamilyDependants, setSelectedFamilyDependants] = useState<Record<number, string>>({});
const [loadingDependants, setLoadingDependants] = useState(false);
const [physicianCheckup, setPhysicianCheckup] = useState<string>('');
const [currentlyTakingMedication, setCurrentlyTakingMedication] = useState<string>('');
const [stoppedMedication, setStoppedMedication] = useState<string>('');
const [otherMedicineSources, setOtherMedicineSources] = useState<string>('');

const [difficultyUrinating, setDifficultyUrinating] = useState<string>('');
const [difficultyStooling, setDifficultyStooling] = useState<string>('');

const [stretching, setStretching] = useState<string>('');
const [aerobicActivity, setAerobicActivity] = useState<string>('');
const [strengthTraining, setStrengthTraining] = useState<string>('');
const [walking, setWalking] = useState<string>('');
const [otherActivity, setOtherActivity] = useState<string>('');

const [littleInterest, setLittleInterest] = useState<string>('');
const [feelingSad, setFeelingSad] = useState<string>('');
const [sleepAppetiteProblems, setSleepAppetiteProblems] = useState<string>('');
const [lowEnergy, setLowEnergy] = useState<string>('');
const [anxiousRestless, setAnxiousRestless] = useState<string>('');

const [workStress, setWorkStress] = useState<string>('');

const [stressReasons, setStressReasons] = useState<string[]>([]);

// Past History - detail text fields
const [chronicIllnessDetail, setChronicIllnessDetail] = useState<string>('');
const [surgeryDetail, setSurgeryDetail] = useState<string>('');

// Sleep Assessment - wake up reason
const [wakeUpReason, setWakeUpReason] = useState<string>('');

// Drinking Habits - sub-questions
const [drinkingDuration, setDrinkingDuration] = useState<string>('');
const [drinkingFrequency, setDrinkingFrequency] = useState<string>('');
const [triedQuitDrinking, setTriedQuitDrinking] = useState<string>('');
const [alcoholQuantity, setAlcoholQuantity] = useState<string>('');
const [quitDrinkingWhen, setQuitDrinkingWhen] = useState<string>('');

// Smoking Habits - sub-questions
const [tobaccoType, setTobaccoType] = useState<string>('');
const [bediCount, setBediCount] = useState<string>('');
const [smokingDuration, setSmokingDuration] = useState<string>('');
const [triedQuitSmoking, setTriedQuitSmoking] = useState<string>('');
const [quitSmokingWhen, setQuitSmokingWhen] = useState<string>('');

// Bowel/Bladder - difficulty type
const [urineDifficultyType, setUrineDifficultyType] = useState<string>('');

// Fitness - other activity text
const [otherActivityText, setOtherActivityText] = useState<string>('');

// Employee Wellness - other stress text
const [stressOtherText, setStressOtherText] = useState<string>('');

// const [showSummary, setShowSummary] = useState(false);
// const [allAssessmentData, setAllAssessmentData] = useState<any>(null);

const [savedData, setSavedData] = useState<SavedDataType | null>(null);
 // For showing on the new page

 type SavedDataType = {
  workStress: string;
  // add other fields later if needed
};


// const handleSubmit = () => {
//   const finalData = {
//     age,
//     gender,
//     heightUnit,
//     heightFeet,
//     heightInches,
//     heightCm,
//     weightUnit,
//     weightValue,
//     bmi,
//     bmiStatus,
//     healthOpinion,
//     selectedIllnesses,
//     hasChronicIllness,
//     hasSurgery,
//     sleepHours,
//     wakeUpMidSleep,
//     morningFeeling,
//     isNonVeg,
//     friedJunkFood,
//     milkDairy,
//     freshFruitsVeg,
//     waterIntake,
//     meatSeafood,
//     consumingAlcohol,
//     consumedInPast,
//     consumingTobacco,
//     quitHabit,
//     familyIllness,
//     physicianCheckup,
//     currentlyTakingMedication,
//     stoppedMedication,
//     otherMedicineSources,
//     difficultyUrinating,
//     difficultyStooling,
//     stretching,
//     aerobicActivity,
//     strengthTraining,
//     walking,
//     otherActivity,
//     littleInterest,
//     feelingSad,
//     sleepAppetiteProblems,
//     lowEnergy,
//     anxiousRestless,
//     workStress
//   };

//   console.log('Final Assessment Data:', finalData);
//   setAllAssessmentData(finalData);
//   setShowSummary(true);
// };







// Mapping helpers: frontend values → backend values
// No mapping needed - frontend now uses backend values directly

const toggleIllness = (illness: string) => {
  // Single-select: toggle off if already selected, otherwise set as the only selection
  setSelectedIllnesses((prev) => {
    const next = prev.includes(illness) ? [] : [illness];
    if (!next.includes('other')) setPresentingIllnessOther('');
    return next;
  });
};


// Fetch dependants for family illness selection
useEffect(() => {
  const fetchDependants = async () => {
    setLoadingDependants(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.dependants.list);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setFamilyDependants(data);
    } catch {
      setFamilyDependants([]);
    } finally {
      setLoadingDependants(false);
    }
  };
  fetchDependants();
}, []);

useEffect(() => {
  let heightInMeters = 0;
  let weightInKg = 0;

  // Convert height to meters
  if (heightUnit === 'cm' && heightCm) {
    heightInMeters = parseFloat(heightCm) / 100;
  } else if (heightUnit === 'feet' && heightFeet) {
    const feet = parseFloat(heightFeet) || 0;
    const inches = parseFloat(heightInches) || 0;
    const totalInches = feet * 12 + inches;
    heightInMeters = totalInches * 0.0254;
  }

  // Weight is always in kg (frontend collects kg only)
  if (weightValue) {
    weightInKg = parseFloat(weightValue);
  }

  // Calculate BMI if values are valid
  if (heightInMeters > 0 && weightInKg > 0) {
    const calculatedBmi = weightInKg / (heightInMeters * heightInMeters);
    const roundedBmi = calculatedBmi.toFixed(1);
    setBmi(roundedBmi);

    // Determine BMI status
    if (calculatedBmi < 18.5) {
      setBmiStatus('Underweight');
    } else if (calculatedBmi < 24.9) {
      setBmiStatus('Normal');
    } else if (calculatedBmi < 29.9) {
      setBmiStatus('Overweight');
    } else {
      setBmiStatus('Obese');
    }
  } else {
    setBmi('');
    setBmiStatus('');
  }
}, [heightUnit, heightFeet, heightInches, heightCm, weightValue]);


  const [carouselIndex, setCarouselIndex] = useState(0);
  const RECORDS_VISIBLE = 3;

  // Date range filter for past assessments
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const filteredRecords = React.useMemo(() => {
    if (!filterFrom && !filterTo) return assessmentRecords;
    return assessmentRecords.filter(r => {
      const d = r.created_at?.split('T')[0];
      if (!d) return true;
      if (filterFrom && d < filterFrom) return false;
      if (filterTo && d > filterTo) return false;
      return true;
    });
  }, [assessmentRecords, filterFrom, filterTo]);

  // Reset carousel when filter changes
  React.useEffect(() => { setCarouselIndex(0); }, [filterFrom, filterTo]);

  const handlePrev = () => {
    setCarouselIndex(prev => (prev === 0 ? Math.max(0, filteredRecords.length - RECORDS_VISIBLE) : prev - 1));
  };

  const handleNext = () => {
    setCarouselIndex(prev => (prev >= filteredRecords.length - RECORDS_VISIBLE ? 0 : prev + 1));
  };

  const getVisibleRecords = () => {
    return filteredRecords.slice(carouselIndex, carouselIndex + RECORDS_VISIBLE);
  };

  // Reset all form state for a fresh assessment
  const resetFormState = useCallback(() => {
    setMoodValue(2);
    setHeightUnit('feet'); setHeightFeet(''); setHeightInches(''); setHeightCm('');
    setWeightValue(''); setBmi(''); setBmiStatus('');
    setHealthOpinion(''); setShowUnhealthyPopup(false);
    setSelectedIllnesses([]);
    setPresentingIllnessOther('');
    setHasChronicIllness(''); setHasSurgery('');
    setChronicIllnessDetail(''); setSurgeryDetail('');
    setSleepHours('' as any); setWakeUpMidSleep(''); setMorningFeeling(''); setWakeUpReason('');
    setIsNonVeg(false); setFriedJunkFood(''); setMilkDairy(''); setFreshFruitsVeg(''); setWaterIntake(''); setMeatSeafood('');
    setConsumingAlcohol(''); setConsumedInPast('');
    setDrinkingDuration(''); setDrinkingFrequency(''); setTriedQuitDrinking(''); setAlcoholQuantity(''); setQuitDrinkingWhen('');
    setConsumingTobacco(''); setQuitHabit('');
    setTobaccoType(''); setBediCount(''); setSmokingDuration(''); setTriedQuitSmoking(''); setQuitSmokingWhen('');
    setFamilyIllness(''); setSelectedFamilyDependants({}); setPhysicianCheckup(''); setCurrentlyTakingMedication(''); setStoppedMedication(''); setOtherMedicineSources('');
    setDifficultyUrinating(''); setDifficultyStooling(''); setUrineDifficultyType('');
    setStretching(''); setAerobicActivity(''); setStrengthTraining(''); setWalking(''); setOtherActivity(''); setOtherActivityText('');
    setLittleInterest(''); setFeelingSad(''); setSleepAppetiteProblems(''); setLowEnergy(''); setAnxiousRestless('');
    setWorkStress(''); setStressReasons([]); setStressOtherText('');
    setValidationError('');
  }, []);

  useEffect(() => {
    setCurrentAssessmentId(null);
    setSubmittedReportData(null);
    setDiseasePrediction(null);
    setSelectedUserName('');
    setAge(0);
    setGender('');
    setSavingProgress(false);
    setShowNewAssessmentConfirm(false);
    setPendingAssessmentCount(0);
    setShowSubmitConfirm(false);
    setFilterFrom('');
    setFilterTo('');
    setCarouselIndex(0);
    resetFormState();
    setView(currentView => currentView === 'records' ? 'records' : 'initial');
  }, [activeProfileKey, resetFormState]);

  const handleStartNewAssessment = () => {
    const pending = assessmentRecords.filter(r => r.status === 'in_process').length;
    if (pending > 0) {
      setPendingAssessmentCount(pending);
      setShowNewAssessmentConfirm(true);
      return;
    }
    proceedNewAssessment();
  };

  // Helper: start a new assessment for self, auto-fetch prefill, navigate to moodAssessment
  const proceedNewAssessment = async () => {
    const requestProfileKey = activeProfileKey;
    setShowNewAssessmentConfirm(false);
    try {
      setSavingProgress(true);
      resetFormState();
      // Fetch prefill for self
      const prefillData = await healthAssessmentService.getPrefill();
      if (activeProfileKeyRef.current !== requestProfileKey) return;
      if (prefillData.age) setAge(prefillData.age);
      if (prefillData.gender) {
        setGender(prefillData.gender);
      }
      setSelectedUserName(prefillData.name || 'Self');
      // Create assessment for self
      const assessment = await createNewAssessment('self');
      if (assessment) {
        // Save the prefilled age/gender
        const saved = await saveStepProgress({
          age: prefillData.age,
          gender: prefillData.gender || 'M',
          current_step: 1
        }, assessment.id);
        if (saved) setView('moodAssessment');
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
      if (activeProfileKeyRef.current === requestProfileKey) {
        showToast('Failed to start assessment. Please try again.');
      }
    } finally {
      if (activeProfileKeyRef.current === requestProfileKey) {
        setSavingProgress(false);
      }
    }
  };

  const locationData = [
    { name: 'New Delhi', img: '/DELHI-8.png' },
    { name: 'Chandigarh', img: '/Chandigarh.png' },
    { name: 'Srinagar', img: '/srinagr.png' },
    { name: 'Cochin', img: '/kochi.png' },
    { name: 'Bangalore', img: '/BANGALORE-8.png' },
    { name: 'Mumbai', img: '/mumbai.png' },
    { name: 'Kolkata', img: '/KOLKATA-8.png' },
    { name: 'Ahmedabad', img: '/AHEMDABAD-8.png' },
    { name: 'Jaipur', img: '/JAIPUR-8.png' },
    { name: 'Lucknow', img: '/LUCKNOW-8.png' },
  ];
  const LOCATIONS_VISIBLE = 4;
  const [locationCarouselIndex, setLocationCarouselIndex] = React.useState(0);
  const handleLocationPrev = () => {
    setLocationCarouselIndex(prev => prev === 0 ? locationData.length - LOCATIONS_VISIBLE : prev - 1);
  };
  const handleLocationNext = () => {
    setLocationCarouselIndex(prev => prev >= locationData.length - LOCATIONS_VISIBLE ? 0 : prev + 1);
  };
  // Auto-rotate carousel every 2 seconds
useEffect(() => {
  const interval = setInterval(() => {
    setLocationCarouselIndex(prev =>
      prev >= locationData.length - LOCATIONS_VISIBLE ? 0 : prev + 1
    );
  }, 2000); // Rotate every 2 seconds

  return () => clearInterval(interval); // Cleanup on unmount
}, []);
  const getVisibleLocations = () => {
    const visible = [];
    for (let i = 0; i < LOCATIONS_VISIBLE; i++) {
      visible.push(locationData[(locationCarouselIndex + i) % locationData.length]);
    }
    return visible;
  };
/* basic profile */ 
// if (showSummary && allAssessmentData) {
//   return (
//     <SummaryPage
//       data={allAssessmentData}
//       onBackToStart={() => {
//         setShowSummary(false);
//         setView('records'); 
//       }}
//     />
//   );
// }

/* basic profile ends */
  if (view === 'records') {
    return (
      <div className="assessment-records-page">
        <div className="ha-form-section-header ha-form-section-header--teal" style={{ maxWidth: 1200, margin: '0 auto 20px', marginLeft: 'auto', marginRight: 'auto' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
            <FontAwesomeIcon icon={faClipboardCheck} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Health Assessment Records</h2>
            <p>View, manage, and download your health assessment reports</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="healthassesment bng.png" alt="Health Assessment Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Your Assessments</h2>
              <p>Add your Health assessment Records</p>
            </div>

            {/* Date range filter */}
            {assessmentRecords.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b', fontFamily: 'Nunito, sans-serif' }}>Filter:</span>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={e => setFilterFrom(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.82rem', fontFamily: 'Nunito, sans-serif', color: '#334155', outline: 'none' }}
                  title="From date"
                />
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>to</span>
                <input
                  type="date"
                  value={filterTo}
                  onChange={e => setFilterTo(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.82rem', fontFamily: 'Nunito, sans-serif', color: '#334155', outline: 'none' }}
                  title="To date"
                />
                {(filterFrom || filterTo) && (
                  <button
                    onClick={() => { setFilterFrom(''); setFilterTo(''); }}
                    style={{ padding: '5px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: '0.78rem', fontWeight: 700, color: '#ef4444', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}
                  >
                    Clear
                  </button>
                )}
                {(filterFrom || filterTo) && (
                  <span style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'Nunito, sans-serif' }}>
                    Showing {filteredRecords.length} of {assessmentRecords.length}
                  </span>
                )}
              </div>
            )}

            {loadingRecords ? (
              <p style={{ padding: '20px', textAlign: 'center' }}>Loading assessments...</p>
            ) : filteredRecords.length === 0 ? (
              <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No health assessments found. Click "New Health Evaluation" to start one.
              </p>
            ) : (
              <div className="assessment-carousel-wrapper">
                {filteredRecords.length > RECORDS_VISIBLE && (
                  <button className="carousel-arrow prev" onClick={handlePrev}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                )}
                <div className="assessment-carousel">
                  {getVisibleRecords().map((record, index) => (
                    <div className="assessment-card" key={record.id || index}>
                      <div className="assessment-card-header">
                        <div>
                          <span className="record-name">
                            {typeof record.for_whom === 'object' ? record.for_whom.name : (record.dependant_data?.name || 'Self')}
                          </span>
                          <span className="record-relation">
                            {typeof record.for_whom === 'object' ? record.for_whom.type : (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                          </span>
                        </div>
                        {record.risk_category && (
                          <span className={`assessment-card-risk-badge risk-${(record.risk_category || '').toLowerCase()}`}>
                            {(record.risk_category || '').charAt(0).toUpperCase() + (record.risk_category || '').slice(1)}
                          </span>
                        )}
                      </div>
                      <div className="assessment-card-info">
                        <p><strong>Created On:</strong> {new Date(record.created_at).toLocaleString()}</p>
                        <p><strong>Status:</strong> {record.status === 'active' ? 'Completed' : 'In Progress'}</p>
                      </div>
                      <div className="assessment-card-actions">
                        {record.status === 'active' ? (
                        <button
                          className="download-btn"
                          onClick={() => viewAssessmentReport(record)}
                        >
                          <FontAwesomeIcon icon={faEye} /> View Assessment
                        </button>
                        ) : (
                        <button
                          className="download-btn resume-btn"
                          onClick={() => resumeAssessment(record)}
                        >
                          <FontAwesomeIcon icon={faPlayCircle} /> Resume
                        </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {filteredRecords.length > RECORDS_VISIBLE && (
                   <button className="carousel-arrow next" onClick={handleNext}>
                      <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                )}
              </div>
            )}

            {/* Health Analytics Section */}
            {(() => {
              const completedAssessments = assessmentRecords
                .filter(r => r.status === 'active')
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

              if (completedAssessments.length === 0) return null;

              const latestAssessment = completedAssessments[completedAssessments.length - 1];
              const previousAssessment = completedAssessments.length >= 2
                ? completedAssessments[completedAssessments.length - 2]
                : null;

              const toWellness = (t: number | null | undefined) =>
                t == null ? 0 : Math.max(0, Math.min(100, 100 - t));

              const latestScore = toWellness(latestAssessment.total_score);
              const previousScore = previousAssessment
                ? toWellness(previousAssessment.total_score)
                : null;
              const scoreDiff = previousScore !== null ? latestScore - previousScore : null;

              // Risk distribution for pie chart
              const riskCounts = completedAssessments.reduce((acc, r) => {
                const cat = (r.risk_category || 'low').toLowerCase();
                acc[cat] = (acc[cat] || 0) + 1;
                return acc;
              }, {} as Record<string, number>);

              const RISK_COLORS: Record<string, string> = {
                low: '#10b981',
                medium: '#f59e0b',
                high: '#ef4444',
              };

              const pieData = Object.entries(riskCounts).map(([name, value]) => ({
                name: name.charAt(0).toUpperCase() + name.slice(1),
                value,
                color: RISK_COLORS[name] || '#94a3b8',
              }));

              // Line chart data
              const trendData = completedAssessments.map(r => {
                const d = new Date(r.created_at);
                return {
                  date: `${d.getDate().toString().padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`,
                  score: toWellness(r.total_score),
                  risk: r.risk_category || 'N/A',
                  fullDate: d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                };
              });

              const latestRisk = (latestAssessment.risk_category || 'low').toLowerCase();

              return (
                <div className="ha-analytics-section">
                  <div className="ha-analytics-header">
                    <div className="ha-analytics-header-icon">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div>
                      <h3 className="ha-analytics-title">Your Health Analytics</h3>
                      <p className="ha-analytics-subtitle">Insights from your completed assessments</p>
                    </div>
                  </div>

                  <div className="ha-analytics-grid">
                    {/* 1. Wellness Score Trend */}
                    <div className="ha-analytics-card ha-analytics-card--trend">
                      <h4 className="ha-analytics-card-title">Wellness Score Trend</h4>
                      <p className="ha-analytics-card-hint">Higher is healthier &middot; out of 100</p>
                      <div className="ha-analytics-chart-wrap">
                        {completedAssessments.length === 1 ? (
                          <div className="ha-analytics-single-score">
                            <div className="ha-analytics-single-score-value">{latestScore}</div>
                            <div className="ha-analytics-single-score-label">Wellness Score</div>
                            <div className="ha-analytics-single-score-date">{trendData[0]?.fullDate}</div>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                              <defs>
                                <linearGradient id="scoreGradient" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#06b6d4" />
                                  <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                              </defs>
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={{ stroke: '#e2e8f0' }}
                                tickLine={false}
                              />
                              <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={35}
                              />
                              <Tooltip
                                contentStyle={{
                                  background: '#ffffff',
                                  border: 'none',
                                  borderRadius: '12px',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                  padding: '10px 14px',
                                  fontFamily: 'Nunito, sans-serif',
                                  fontSize: '13px',
                                }}
                                formatter={(value: any, _name: any, props: any) => {
                                  const risk = props.payload?.risk || '';
                                  return [`Wellness: ${value}/100  |  Risk: ${risk}`, ''];
                                }}
                                labelFormatter={(label: any) => label}
                              />
                              <Line
                                type="monotone"
                                dataKey="score"
                                stroke="url(#scoreGradient)"
                                strokeWidth={3}
                                dot={{ r: 5, fill: '#06b6d4', stroke: '#ffffff', strokeWidth: 2 }}
                                activeDot={{ r: 7, fill: '#8b5cf6', stroke: '#ffffff', strokeWidth: 2 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* 2. Risk Level Summary (Donut) */}
                    <div className="ha-analytics-card ha-analytics-card--donut">
                      <h4 className="ha-analytics-card-title">Risk Distribution</h4>
                      <p className="ha-analytics-card-hint">How your assessments break down by risk level</p>
                      <div className="ha-analytics-chart-wrap ha-analytics-donut-wrap">
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={75}
                              paddingAngle={3}
                              dataKey="value"
                              stroke="none"
                            >
                              {pieData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                background: '#ffffff',
                                border: 'none',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                padding: '8px 14px',
                                fontFamily: 'Nunito, sans-serif',
                                fontSize: '13px',
                              }}
                              formatter={(value: any, name: any) => [`${value} assessment${value > 1 ? 's' : ''}`, name]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="ha-analytics-donut-center">
                          <span className="ha-analytics-donut-center-label">Latest</span>
                          <span
                            className="ha-analytics-donut-center-value"
                            style={{ color: RISK_COLORS[latestRisk] || '#64748b' }}
                          >
                            {latestRisk.charAt(0).toUpperCase() + latestRisk.slice(1)}
                          </span>
                        </div>
                        <div className="ha-analytics-legend">
                          {pieData.map((entry, idx) => (
                            <div className="ha-analytics-legend-item" key={idx}>
                              <span className="ha-analytics-legend-dot" style={{ background: entry.color }} />
                              <span className="ha-analytics-legend-text">{entry.name} ({entry.value})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 3. Score Comparison Card */}
                    <div className="ha-analytics-card ha-analytics-card--comparison">
                      <h4 className="ha-analytics-card-title">Latest Wellness Score</h4>
                      <p className="ha-analytics-card-hint">Same score you see on the assessment report</p>
                      <div className="ha-analytics-comparison-content">
                        <div className="ha-analytics-big-score">{latestScore}</div>
                        <div className="ha-analytics-score-label">out of 100 &middot; higher is better</div>

                        {scoreDiff !== null && (
                          <div className={`ha-analytics-score-change ${scoreDiff >= 0 ? 'improved' : 'worsened'}`}>
                            <FontAwesomeIcon icon={scoreDiff >= 0 ? faArrowUp : faArrowDown} />
                            <span>{scoreDiff > 0 ? '+' : ''}{scoreDiff} pts</span>
                            <span className="ha-analytics-change-note">vs previous</span>
                          </div>
                        )}

                        <div
                          className="ha-analytics-risk-badge"
                          style={{
                            background: `${RISK_COLORS[latestRisk] || '#94a3b8'}18`,
                            color: RISK_COLORS[latestRisk] || '#64748b',
                            border: `1.5px solid ${RISK_COLORS[latestRisk] || '#94a3b8'}40`,
                          }}
                        >
                          {latestRisk.charAt(0).toUpperCase() + latestRisk.slice(1)} Risk
                        </div>

                        <div className="ha-analytics-total-count">
                          <span className="ha-analytics-total-count-number">{completedAssessments.length}</span>
                          <span className="ha-analytics-total-count-label">Assessment{completedAssessments.length > 1 ? 's' : ''} Completed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

             <div className="panel-actions">
                <button className="action-btn new-eval-records" onClick={handleStartNewAssessment} disabled={savingProgress}>{savingProgress ? 'Starting...' : 'New Health Evaluation'}</button>
            </div>
          </div>
        </div>
        <div className="home-btn-container">
            <button className="action-btn home-btn" onClick={() => navigate('/')}>Home</button>
        </div>
      </div>
    );
  }


{/* next view */}
if (view === 'moodAssessment') {
  return (

  <div className="assessment-records-page">
      <StepProgressBar currentStep="moodAssessment" onConfirmExit={handleExitAssessment} />
      <div className="ha-form-section-header ha-form-section-header--amber" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
        <div className="ha-form-section-header-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
          <FontAwesomeIcon icon={faSmile} />
        </div>
        <div className="ha-form-section-header-text">
          <h2>Mood Assessment</h2>
          <p>How are you feeling today? This helps us understand your current state</p>
        </div>
      </div>
      <div className="assessment-records-container">
          <div className="assessment-illustration">
              <img src="/Drag.png" alt="How are you doing illustration" />
          </div>
          <div className="assessment-records-panel">
              <div className="panel-header">
                  <h2>Your Mood Today</h2>
                  <p>Please enter correct information for us to provide accurate output</p>
              </div>

              <div className="mood-assessment-content">
                  <h3>Hi {selectedUserName || 'there'}, How are you doing Today</h3>
                  <p>Drag the slider</p>
                  <div className="mood-slider-container">
                 {/* Mood icons above the slider */}
                  <div className="mood-icons">
                   <img src="/drag-1.png" alt="Very Sad" className="mood-icon" />
                   <img src="/drag-2.png" alt="Sad" className="mood-icon" />
                   <img src="/drag-3.png" alt="Neutral" className="mood-icon" />
                   <img src="/drag-4.png" alt="Happy" className="mood-icon" />
                   <img src="/drag-5.png" alt="Very Happy" className="mood-icon" />
                   </div>
                  {/* Mood slider below the icons */}
                  <input type="range" min="0" max="4" value={moodValue}
                   onChange={(e) => setMoodValue(parseInt(e.target.value))}
                    className={`mood-slider mood-${moodValue}`} />
                  </div>
               </div>
          </div>
      </div>
      <div className="page-navigation-buttons page-navigation-buttons--single">
        <button
          className="action-btn next-btn"
          disabled={savingProgress}
          onClick={async () => {
            setValidationError('');
            const saved = await saveStepProgress({
              mood_today: moodValue + 1,
              current_step: 2
            });
             if (saved) goToView('basicProfile');
          }}
        >
          {savingProgress ? 'Saving...' : 'Next'}
        </button>
      </div>
  </div>
)}
if (view === 'basicProfile') {
  return (
    <div className="assessment-records-page">
      <StepProgressBar currentStep="basicProfile" onConfirmExit={handleExitAssessment} />
      <div className="ha-form-section-header ha-form-section-header--blue" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
        <div className="ha-form-section-header-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
          <FontAwesomeIcon icon={faIdCard} />
        </div>
        <div className="ha-form-section-header-text">
          <h2>Basic Profile</h2>
          <p>Your physical measurements and general health opinion</p>
        </div>
      </div>
      <div className="assessment-records-container">
        <div className="assessment-illustration">
          <img src="/basic.png" alt="Basic Profile Illustration" />
        </div>
        <div className="assessment-records-panel">
          <div className="panel-header">
            <h2>Body Measurements</h2>
            <p>Please enter correct information for us to provide accurate output</p>
          </div>

          <div className="basic-profile-content">
           {/* Height Section */}
<div className="form-section">
  <label className="height-label">My Height</label>
  <div className={`unit-switch ${heightUnit === 'cm' ? 'cm' : ''}`}>
    <input
      type="radio"
      id="feet"
      name="heightUnit"
      checked={heightUnit === 'feet'}
      onChange={() => setHeightUnit('feet')}
    />
    <input
      type="radio"
      id="cm"
      name="heightUnit"
      checked={heightUnit === 'cm'}
      onChange={() => setHeightUnit('cm')}
    />
    <span className="slider" />
    <label htmlFor="feet">Feet</label>
    <label htmlFor="cm">Cm</label>
  </div>

  <div className="height-inputs">
    {heightUnit === 'feet' ? (
      <>
        <input
          type="number"
          placeholder="Feet"
          value={heightFeet}
          onChange={(e) => setHeightFeet(e.target.value)}
        />
        <input
          type="number"
          placeholder="Inches"
          value={heightInches}
          onChange={(e) => setHeightInches(e.target.value)}
        />
      </>
    ) : (
      <input
        type="number"
        placeholder="Cm"
        value={heightCm}
        onChange={(e) => setHeightCm(e.target.value)}
      />
    )}
  </div>
</div>

{/* Weight Section (kg only) */}
<div className="form-section">
  <label className="height-label">My Weight (Kg)</label>
  <div className="weight-inputs">
    <input
      type="number"
      placeholder="Kg"
      value={weightValue}
      onChange={(e) => setWeightValue(e.target.value)}
    />
  </div>
</div>
            {/* BMI Section (Display only) */}
            <div className="bmi-section">
  <label>BMI</label>
  <input
    type="text"
    className="bmi-input"
    value={bmi ? `${bmi} (${bmiStatus})` : ''}
    readOnly
  />
</div>
              {/* General Health Opinion Section */}
            <div className="form-section health-opinion-section">
                <p>In general, what is your opinion about your health?</p>
                <div className="health-options">
                  <div className="health-option">
                     <img src="/healty1.png" alt="Extremely Healthy" className="health-image" />
                      <button className={`health-option-btn ${healthOpinion === 'extremely_healthy' ? 'selected' : ''}`}
                           onClick={() => setHealthOpinion('extremely_healthy')}> Extremely Healthy </button>
                   </div>
                  <div className="health-option">
                     <img src="/healty1.png" alt="Healthy" className="health-image" />
                      <button className={`health-option-btn ${healthOpinion === 'healthy' ? 'selected' : ''}`}
                           onClick={() => setHealthOpinion('healthy')}> Healthy </button>
                   </div>
                      <div className="health-option">
                       <img src="/unhealty1.png" alt="Unhealthy" className="health-image" />
                       <button className={`health-option-btn ${healthOpinion === 'unhealthy' ? 'selected' : ''}`}
                       onClick={() => {
                       setHealthOpinion('unhealthy');
                       setShowUnhealthyPopup(true);}}>Unhealthy</button>
                    </div>
                </div>
                {showUnhealthyPopup && (
  <div className="popup-overlay">
    <div className="popup-content">
      <button className="popup-close" onClick={() => setShowUnhealthyPopup(false)}>×</button>
      <h3>YOU NEED TO CONNECT WITH DOCTOR</h3>
      <div className="popup-actions">
        <button className="popup-btn" onClick={() => { setShowUnhealthyPopup(false); showToast('We will connect you with a doctor soon.', 'success'); }}>Yes</button>
        <button className="popup-btn" onClick={() => setShowUnhealthyPopup(false)}>No</button>
      </div>
    </div>
  </div>
)}
      </div>
          </div>
        </div>
      </div>
          {validationError && (
            <div className="ha-validation-error">
              <p>{validationError}</p>
            </div>
          )}
          <div className="page-navigation-buttons">
        <button className="action-btn prev-btn" onClick={() => goToView('moodAssessment')}>Previous</button>
        <button
          className="action-btn next-btn"
          disabled={savingProgress}
          onClick={async () => {
            if (heightUnit === 'feet' && (!heightFeet || !heightInches)) {
              setValidationError('Please enter your height (feet and inches)');
              return;
            }
            if (heightUnit === 'cm' && !heightCm) {
              setValidationError('Please enter your height in cm');
              return;
            }
            if (!weightValue) {
              setValidationError('Please enter your weight');
              return;
            }
            if (!healthOpinion) {
              setValidationError('Please select your health opinion');
              return;
            }
            setValidationError('');
            const weightInKg = parseFloat(weightValue);
            const bmiNumeric = bmi ? parseFloat(bmi) : undefined;
            const saved = await saveStepProgress({
              height_unit: heightUnit,
              height_feet: heightUnit === 'feet' ? parseFloat(heightFeet) || 0 : undefined,
              height_inches: heightUnit === 'feet' ? parseFloat(heightInches) || 0 : undefined,
              height_cm: heightUnit === 'cm' ? parseFloat(heightCm) || 0 : undefined,
              weight_kg: weightInKg || 0,
              bmi_value: bmiNumeric,
              bmi: bmiStatus ? bmiStatus.toLowerCase() : undefined,
              health_opinion: healthOpinion || undefined,
              current_step: 3
            });
             if (saved) goToView('presentingIllness');
          }}
        >
          {savingProgress ? 'Saving...' : 'Next'}
        </button>
      </div>
      </div>
 )}
  if (view === 'presentingIllness') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="presentingIllness" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--rose" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(244, 63, 94, 0.1)', color: '#f43f5e' }}>
            <FontAwesomeIcon icon={faVirus} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Presenting Illness</h2>
            <p>Select any current acute or chronic conditions you are experiencing</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
          <img src="/illness.png" alt="Presenting Illness Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Current Conditions</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="presenting-illness-content">
              <p className="illness-question">Any Acute Illness / Chronic Illness?</p>
              <div className="illness-options-grid">
                {/* Each of these buttons represents an illness option */}
                <button className={`illness-option-btn ${selectedIllnesses.includes('cough') ? 'selected' : ''}`} onClick={() => toggleIllness('cough')}>
                  <img src="/Cough.svg" alt="Cough" />
                  <span>Cough</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('cold') ? 'selected' : ''}`} onClick={() => toggleIllness('cold')}>
                  <img src="/Cold.svg" alt="Cold" /> 
                  <span>Cold</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('fever') ? 'selected' : ''}`} onClick={() => toggleIllness('fever')}>
                  <img src="/Fever.svg" alt="Fever" />
                  <span>Fever</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('diabetes') ? 'selected' : ''}`} onClick={() => toggleIllness('diabetes')}>
                  <img src="/Diabetes.svg" alt="Diabetes" /> 
                  <span>Diabetes</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('hypertension') ? 'selected' : ''}`} onClick={() => toggleIllness('hypertension')}>
                  <img src="/Hypertension.svg" alt="Hypertension" />
                  <span>Hypertension</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('asthma') ? 'selected' : ''}`} onClick={() => toggleIllness('asthma')}>
                  <img src="/Asthma.svg" alt="Asthma" /> 
                  <span>Asthma</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('flu') ? 'selected' : ''}`} onClick={() => toggleIllness('flu')}>
                  <img src="/Flu.svg" alt="Flu" onError={(e) => { (e.target as HTMLImageElement).src = '/Cold.svg'; }} />
                  <span>Flu</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('other') ? 'selected' : ''}`} onClick={() => toggleIllness('other')}>
                  <img src="/Other.svg" alt="Other" />
                  <span>Other</span>
                </button>
                <button className={`illness-option-btn ${selectedIllnesses.includes('fine') ? 'selected' : ''}`} onClick={() => toggleIllness('fine')}>
                  <img src="/Fine.svg" alt="I am Fine" />
                  <span>I am Fine</span>
                </button>
                 </div>
              {selectedIllnesses.includes('other') && (
                <div className="conditional-detail-input" style={{ marginTop: 16 }}>
                  <label className="detail-input-label">Please specify the illness</label>
                  <input
                    type="text"
                    className="detail-text-input"
                    placeholder="e.g., Migraine, Allergy..."
                    maxLength={255}
                    value={presentingIllnessOther}
                    onChange={(e) => setPresentingIllnessOther(e.target.value)}
                  />
                </div>
              )}
            </div> {/* End presenting-illness-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('basicProfile')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (selectedIllnesses.length === 0) {
                setValidationError('Please select at least one condition');
                return;
              }
              if (selectedIllnesses.includes('other') && !presentingIllnessOther.trim()) {
                setValidationError('Please specify the illness');
                return;
              }
              setValidationError('');
              const isOther = selectedIllnesses.includes('other');
              const saved = await saveStepProgress({
                presenting_illness: selectedIllnesses.length > 0 ? selectedIllnesses[0] : 'fine',
                presenting_illness_other: isOther ? presentingIllnessOther.trim() : '',
                current_step: 4
              });
               if (saved) goToView('pastHistory');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'pastHistory') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="pastHistory" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--indigo" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
            <FontAwesomeIcon icon={faHistory} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Past History</h2>
            <p>Any previous medical history that helps us assess your health better</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/past.png" alt="Past History Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Medical History</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="past-history-content">
              {/* Chronic Illness Section */}
              <div className="past-history-question-group">
                <p className="question-text">History of any chronic illness</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="chronicIllness"
                      value="yes"
                      checked={hasChronicIllness === 'yes'}
                      onChange={() => setHasChronicIllness('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="chronicIllness"
                      value="no"
                      checked={hasChronicIllness === 'no'}
                      onChange={() => { setHasChronicIllness('no'); setChronicIllnessDetail(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
                {hasChronicIllness === 'yes' && (
                  <div className="conditional-detail-input" style={{ marginTop: 12, marginLeft: 24 }}>
                    <label className="detail-input-label">Please specify the chronic illness</label>
                    <input
                      type="text"
                      className="detail-text-input"
                      placeholder="e.g., Diabetes, Hypertension..."
                      value={chronicIllnessDetail}
                      onChange={(e) => setChronicIllnessDetail(e.target.value)}
                    />
                  </div>
                )}
              </div>
  
              {/* Surgery History Section */}
              <div className="past-history-question-group">
                <p className="question-text">History of any surgery</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="hasSurgery"
                      value="yes"
                      checked={hasSurgery === 'yes'}
                      onChange={() => setHasSurgery('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="hasSurgery"
                      value="no"
                      checked={hasSurgery === 'no'}
                      onChange={() => { setHasSurgery('no'); setSurgeryDetail(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
                {hasSurgery === 'yes' && (
                  <div className="conditional-detail-input" style={{ marginTop: 12, marginLeft: 24 }}>
                    <label className="detail-input-label">Please specify the surgery</label>
                    <input
                      type="text"
                      className="detail-text-input"
                      placeholder="e.g., Appendectomy, Knee replacement..."
                      value={surgeryDetail}
                      onChange={(e) => setSurgeryDetail(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div> {/* End past-history-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('presentingIllness')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!hasChronicIllness) {
                setValidationError('Please answer the chronic illness question');
                return;
              }
              if (!hasSurgery) {
                setValidationError('Please answer the surgery history question');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                chronic_illness: hasChronicIllness === 'yes',
                chronic_illness_details: hasChronicIllness === 'yes' ? chronicIllnessDetail : undefined,
                surgery_history: hasSurgery === 'yes',
                surgery_history_details: hasSurgery === 'yes' ? surgeryDetail : undefined,
                current_step: 5
              });
               if (saved) goToView('sleepAssessment');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'sleepAssessment') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="sleepAssessment" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--purple" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
            <FontAwesomeIcon icon={faBed} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Sleep Assessment</h2>
            <p>Understanding your sleep patterns and quality for better health insights</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
         <img src="/sleep.png" alt="Sleep Assessment Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Sleep Patterns</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="sleep-assessment-content">
              {/* Question 1: How many hours do you generally sleep? */}
              <div className="sleep-question-group">
                <p className="question-text">How many hours do you generally sleep in the night</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="sleepHours"
                      value="lt_7"
                      checked={sleepHours === 'lt_7'}
                      onChange={() => setSleepHours('lt_7')}
                    />
                    <span className="radio-indicator"></span>
                    Less than 7 hours
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="sleepHours"
                      value="gt_7"
                      checked={sleepHours === 'gt_7'}
                      onChange={() => setSleepHours('gt_7')}
                    />
                    <span className="radio-indicator"></span>
                    More than 7 hours
                  </label>
                </div>
              </div>
  
              {/* Question 2: Do you wake up in the midst of the sleep? */}
              <div className="sleep-question-group">
                <p className="question-text">Do you wake up in the midst of the sleep for any reason</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="wakeUpMidSleep"
                      value="yes"
                      checked={wakeUpMidSleep === 'yes'}
                      onChange={() => setWakeUpMidSleep('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="wakeUpMidSleep"
                      value="no"
                      checked={wakeUpMidSleep === 'no'}
                      onChange={() => { setWakeUpMidSleep('no'); setWakeUpReason(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>

              {wakeUpMidSleep === 'yes' && (
                <div className="sleep-question-group conditional-subquestion" style={{ marginLeft: 24, marginTop: 12, paddingLeft: 16, borderLeft: '3px solid var(--ha-primary)' }}>
                  <p className="question-text">What's the reason for waking up?</p>
                  <div className="radio-option-group">
                    {[
                      { value: 'excessive_urination', label: 'Excessive Urination' },
                      { value: 'breathing_difficulty', label: 'Breathing Difficulty' },
                      { value: 'stress', label: 'Stress' },
                      { value: 'body_over_heat', label: 'Body Over Heat' },
                      { value: 'indigestion', label: 'Indigestion' },
                      { value: 'anxiety_depression', label: 'Anxiety/Depression' },
                    ].map(opt => (
                      <label key={opt.value} className="custom-radio-label">
                        <input
                          type="radio"
                          name="wakeUpReason"
                          value={opt.value}
                          checked={wakeUpReason === opt.value}
                          onChange={() => setWakeUpReason(opt.value)}
                        />
                        <span className="radio-indicator"></span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
  
              {/* Question 3: Do you generally have a feeling of restlessness or tiredness or irritability? */}
              <div className="sleep-question-group">
                <p className="question-text">Do you generally have a feeling of restlessness or tiredness or irritability in the morning, when you wake up</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="morningFeeling"
                      value="yes"
                      checked={morningFeeling === 'yes'}
                      onChange={() => setMorningFeeling('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="morningFeeling"
                      value="no"
                      checked={morningFeeling === 'no'}
                      onChange={() => setMorningFeeling('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
            </div> {/* End sleep-assessment-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('pastHistory')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!sleepHours) {
                setValidationError('Please select your sleep hours');
                return;
              }
              if (!wakeUpMidSleep) {
                setValidationError('Please answer the wake up question');
                return;
              }
              if (wakeUpMidSleep === 'yes' && !wakeUpReason) {
                setValidationError('Please select the reason for waking up');
                return;
              }
              if (!morningFeeling) {
                setValidationError('Please answer the morning feeling question');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                sleep_hours: sleepHours,
                wakeup_midnight: wakeUpMidSleep === 'yes',
                wakeup_midnight_reasons: wakeUpMidSleep === 'yes' ? wakeUpReason : undefined,
                tired_morning: morningFeeling === 'yes',
                current_step: 6
              });
               if (saved) goToView('eatingHabits');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'eatingHabits') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="eatingHabits" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--green" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <FontAwesomeIcon icon={faUtensils} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Eating Habits</h2>
            <p>Your dietary preferences and food consumption patterns</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/eating.png" alt="Eating Habits Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Diet and Nutrition</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="eating-habits-content">
              {/* Veg/Non-veg Toggle */}
              <div className="diet-type-toggle-container">
                <span className="diet-label">Veg</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={isNonVeg} // Assuming true means Non-veg
                    onChange={() => setIsNonVeg(!isNonVeg)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="diet-label">Non-veg</span>
              </div>
  
              {/* Question 1: How frequent do you eat Fried/Junk food - eating outside */}
              <div className="eating-habit-question-group">
                <p className="question-text">How frequent do you eat Fried/Junk food - eating outside</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="friedJunkFood"
                      value="never"
                      checked={friedJunkFood === 'never'}
                      onChange={() => setFriedJunkFood('never')}
                    />
                    <span className="radio-indicator"></span>
                    Never or almost never
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="friedJunkFood"
                      value="occasionally"
                      checked={friedJunkFood === 'occasionally'}
                      onChange={() => setFriedJunkFood('occasionally')}
                    />
                    <span className="radio-indicator"></span>
                    Occasionally
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="friedJunkFood"
                      value="often"
                      checked={friedJunkFood === 'often'}
                      onChange={() => setFriedJunkFood('often')}
                    />
                    <span className="radio-indicator"></span>
                    Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="friedJunkFood"
                      value="very_often"
                      checked={friedJunkFood === 'very_often'}
                      onChange={() => setFriedJunkFood('very_often')}
                    />
                    <span className="radio-indicator"></span>
                    Very Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="friedJunkFood"
                      value="always"
                      checked={friedJunkFood === 'always'}
                      onChange={() => setFriedJunkFood('always')}
                    />
                    <span className="radio-indicator"></span>
                    Always or Almost Always
                  </label>
                </div>
              </div>
  
              {/* Question 2: Milk and dairy products */}
              <div className="eating-habit-question-group">
                <p className="question-text">Milk and dairy products</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="milkDairy"
                      value="never"
                      checked={milkDairy === 'never'}
                      onChange={() => setMilkDairy('never')}
                    />
                    <span className="radio-indicator"></span>
                    Never or almost never
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="milkDairy"
                      value="occasionally"
                      checked={milkDairy === 'occasionally'}
                      onChange={() => setMilkDairy('occasionally')}
                    />
                    <span className="radio-indicator"></span>
                    Occasionally
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="milkDairy"
                      value="often"
                      checked={milkDairy === 'often'}
                      onChange={() => setMilkDairy('often')}
                    />
                    <span className="radio-indicator"></span>
                    Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="milkDairy"
                      value="very_often"
                      checked={milkDairy === 'very_often'}
                      onChange={() => setMilkDairy('very_often')}
                    />
                    <span className="radio-indicator"></span>
                    Very Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="milkDairy"
                      value="always"
                      checked={milkDairy === 'always'}
                      onChange={() => setMilkDairy('always')}
                    />
                    <span className="radio-indicator"></span>
                    Always or Almost Always
                  </label>
                </div>
              </div>
  
              {/* Question 3: Fresh fruits and green vegetable */}
              <div className="eating-habit-question-group">
                <p className="question-text">Fresh fruits and green vegetable</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="freshFruitsVeg"
                      value="never"
                      checked={freshFruitsVeg === 'never'}
                      onChange={() => setFreshFruitsVeg('never')}
                    />
                    <span className="radio-indicator"></span>
                    Never or almost never
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="freshFruitsVeg"
                      value="occasionally"
                      checked={freshFruitsVeg === 'occasionally'}
                      onChange={() => setFreshFruitsVeg('occasionally')}
                    />
                    <span className="radio-indicator"></span>
                    Occasionally
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="freshFruitsVeg"
                      value="often"
                      checked={freshFruitsVeg === 'often'}
                      onChange={() => setFreshFruitsVeg('often')}
                    />
                    <span className="radio-indicator"></span>
                    Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="freshFruitsVeg"
                      value="very_often"
                      checked={freshFruitsVeg === 'very_often'}
                      onChange={() => setFreshFruitsVeg('very_often')}
                    />
                    <span className="radio-indicator"></span>
                    Very Often
                  </label>
                  <label className="custom-radio-label small-gap">
                    <input
                      type="radio"
                      name="freshFruitsVeg"
                      value="always"
                      checked={freshFruitsVeg === 'always'}
                      onChange={() => setFreshFruitsVeg('always')}
                    />
                    <span className="radio-indicator"></span>
                    Always or Almost Always
                  </label>
                </div>
              </div>
  
              {/* Question 4: How many glasses of water do you drink per day */}
              <div className="eating-habit-question-group">
                <p className="question-text">How many glasses of water do you drink per day</p>
                {(() => {
                  const HARD_MAX = 30;
                  const current = waterIntake ? Math.max(0, Math.min(HARD_MAX, parseInt(waterIntake, 10) || 0)) : 0;
                  const set = (n: number) => setWaterIntake(String(Math.max(0, Math.min(HARD_MAX, n))));
                  return (
                    <div className="wi-stepper">
                      <button
                        type="button"
                        className="wi-btn"
                        onClick={() => set(current - 1)}
                        disabled={current <= 0}
                        aria-label="Decrease glasses"
                      >
                        −
                      </button>
                      <div className="wi-count">
                        <span className="wi-count-num">{current}</span>
                        <span className="wi-count-unit">glasses / day</span>
                      </div>
                      <button
                        type="button"
                        className="wi-btn"
                        onClick={() => set(current + 1)}
                        disabled={current >= HARD_MAX}
                        aria-label="Increase glasses"
                      >
                        +
                      </button>
                    </div>
                  );
                })()}
              </div>
              {isNonVeg && (
  <div className="eating-habit-question-group">
    <p className="question-text">Meat and Sea food</p>
    <div className="radio-option-group horizontal-options">
      <label className="custom-radio-label small-gap">
        <input
          type="radio"
          name="meatSeafood"
          value="occasionally"
          checked={meatSeafood === 'occasionally'}
          onChange={() => setMeatSeafood('occasionally')}
        />
        <span className="radio-indicator"></span>
        Occasionally
      </label>
      <label className="custom-radio-label small-gap">
        <input
          type="radio"
          name="meatSeafood"
          value="often"
          checked={meatSeafood === 'often'}
          onChange={() => setMeatSeafood('often')}
        />
        <span className="radio-indicator"></span>
        Often
      </label>
      <label className="custom-radio-label small-gap">
        <input
          type="radio"
          name="meatSeafood"
          value="very_often"
          checked={meatSeafood === 'very_often'}
          onChange={() => setMeatSeafood('very_often')}
        />
        <span className="radio-indicator"></span>
        Very Often
      </label>
      <label className="custom-radio-label small-gap">
        <input
          type="radio"
          name="meatSeafood"
          value="always"
          checked={meatSeafood === 'always'}
          onChange={() => setMeatSeafood('always')}
        />
        <span className="radio-indicator"></span>
        Always or Almost Always
      </label>
    </div>
  </div>
)}
       </div> {/* End eating-habits-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('sleepAssessment')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!friedJunkFood) {
                setValidationError('Please select how often you eat fried/junk food');
                return;
              }
              if (!milkDairy) {
                setValidationError('Please select how often you consume milk/dairy');
                return;
              }
              if (!freshFruitsVeg) {
                setValidationError('Please select how often you eat fruits/vegetables');
                return;
              }
              if (!waterIntake) {
                setValidationError('Please select your daily water intake');
                return;
              }
              if (isNonVeg && !meatSeafood) {
                setValidationError('Please select how often you consume meat/seafood');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                junk_food_freq: friedJunkFood,
                milk_dairy_freq: milkDairy,
                fruits_veg_freq: freshFruitsVeg,
                water_intake: waterIntake ? parseInt(waterIntake, 10) : null,
                is_veg: !isNonVeg,
                non_veg_freq: isNonVeg ? meatSeafood : undefined,
                current_step: 7
              });
               if (saved) goToView('drinkingHabits');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'drinkingHabits') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="drinkingHabits" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--orange" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
            <FontAwesomeIcon icon={faWineGlass} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Drinking Habits</h2>
            <p>Information about alcohol consumption to assess health risks</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/drinking.png" alt="Drinking Habits Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Alcohol Consumption</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="drinking-habits-content">
              {/* Question 1: Do you consuming alcohol? */}
              <div className="drinking-habit-question-group">
                <p className="question-text">Do you consuming alcohol?</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="consumingAlcohol"
                      value="yes"
                      checked={consumingAlcohol === 'yes'}
                      onChange={() => setConsumingAlcohol('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="consumingAlcohol"
                      value="no"
                      checked={consumingAlcohol === 'no'}
                      onChange={() => { setConsumingAlcohol('no'); setDrinkingDuration(''); setDrinkingFrequency(''); setTriedQuitDrinking(''); setAlcoholQuantity(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>

              {/* Conditional sub-questions when consuming alcohol */}
              {consumingAlcohol === 'yes' && (
                <div className="conditional-subquestion-group" style={{ marginLeft: 24, marginTop: 16, paddingLeft: 16, borderLeft: '3px solid #f97316' }}>
                  <div className="drinking-habit-question-group">
                    <p className="question-text">How long have you been drinking?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'lt_1', label: 'Less than 1 year' },
                        { value: '1_5', label: '1-5 years' },
                        { value: '5_10', label: '5-10 years' },
                        { value: 'gt_10', label: 'More than 10 years' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="drinkingDuration" value={opt.value}
                            checked={drinkingDuration === opt.value}
                            onChange={() => setDrinkingDuration(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="drinking-habit-question-group">
                    <p className="question-text">How often do you drink?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly once' },
                        { value: 'occasional', label: 'Occasional drinker' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="drinkingFrequency" value={opt.value}
                            checked={drinkingFrequency === opt.value}
                            onChange={() => setDrinkingFrequency(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="drinking-habit-question-group">
                    <p className="question-text">Have you tried to quit drinking?</p>
                    <div className="radio-option-group">
                      <label className="custom-radio-label">
                        <input type="radio" name="triedQuitDrinking" value="yes"
                          checked={triedQuitDrinking === 'yes'}
                          onChange={() => setTriedQuitDrinking('yes')} />
                        <span className="radio-indicator"></span>
                        Yes
                      </label>
                      <label className="custom-radio-label">
                        <input type="radio" name="triedQuitDrinking" value="no"
                          checked={triedQuitDrinking === 'no'}
                          onChange={() => setTriedQuitDrinking('no')} />
                        <span className="radio-indicator"></span>
                        No
                      </label>
                    </div>
                  </div>

                  <div className="drinking-habit-question-group">
                    <label className="detail-input-label">Alcohol Quantity (per session)</label>
                    <input type="text" className="detail-text-input"
                      placeholder="e.g., 2 pegs, 1 beer..."
                      value={alcoholQuantity}
                      onChange={(e) => setAlcoholQuantity(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Question 2: Have consumed in the past? — only relevant when not currently drinking */}
              {consumingAlcohol === 'no' && (
                <div className="drinking-habit-question-group">
                  <p className="question-text">Have consumed in the past?</p>
                  <div className="radio-option-group">
                    <label className="custom-radio-label">
                      <input
                        type="radio"
                        name="consumedInPast"
                        value="yes"
                        checked={consumedInPast === 'yes'}
                        onChange={() => setConsumedInPast('yes')}
                      />
                      <span className="radio-indicator"></span>
                      Yes
                    </label>
                    <label className="custom-radio-label">
                      <input
                        type="radio"
                        name="consumedInPast"
                        value="no"
                        checked={consumedInPast === 'no'}
                        onChange={() => { setConsumedInPast('no'); setQuitDrinkingWhen(''); }}
                      />
                      <span className="radio-indicator"></span>
                      No
                    </label>
                  </div>
                </div>
              )}

              {/* Conditional: When did you quit drinking? */}
              {consumingAlcohol === 'no' && consumedInPast === 'yes' && (
                <div className="conditional-subquestion-group" style={{ marginLeft: 24, marginTop: 12, paddingLeft: 16, borderLeft: '3px solid #f97316' }}>
                  <div className="drinking-habit-question-group">
                    <p className="question-text">When did you quit drinking?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'lt_1', label: 'Less than 1 year ago' },
                        { value: '1_3', label: '1-3 years ago' },
                        { value: '3_5', label: '3-5 years ago' },
                        { value: 'gt_5', label: 'More than 5 years ago' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="quitDrinkingWhen" value={opt.value}
                            checked={quitDrinkingWhen === opt.value}
                            onChange={() => setQuitDrinkingWhen(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div> {/* End drinking-habits-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('eatingHabits')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!consumingAlcohol) {
                setValidationError('Please answer the alcohol consumption question');
                return;
              }
              if (consumingAlcohol === 'yes') {
                if (!drinkingDuration) {
                  setValidationError('Please select how long you have been drinking');
                  return;
                }
                if (!drinkingFrequency) {
                  setValidationError('Please select how often you drink');
                  return;
                }
                if (!triedQuitDrinking) {
                  setValidationError('Please answer the quit drinking question');
                  return;
                }
              }
              if (consumingAlcohol === 'no') {
                if (!consumedInPast) {
                  setValidationError('Please answer the past consumption question');
                  return;
                }
                if (consumedInPast === 'yes' && !quitDrinkingWhen) {
                  setValidationError('Please select when you quit drinking');
                  return;
                }
              }
              setValidationError('');
              const isCurrentDrinker = consumingAlcohol === 'yes';
              const saved = await saveStepProgress({
                alcohol_current: isCurrentDrinker,
                alcohol_duration: isCurrentDrinker ? drinkingDuration : undefined,
                alcohol_frequency: isCurrentDrinker ? drinkingFrequency : undefined,
                alcohol_planning_quit: isCurrentDrinker ? triedQuitDrinking === 'yes' : undefined,
                alcohol_quantity: isCurrentDrinker ? alcoholQuantity : undefined,
                alcohol_past: isCurrentDrinker ? false : consumedInPast === 'yes',
                alcohol_quit_period: !isCurrentDrinker && consumedInPast === 'yes' ? quitDrinkingWhen : undefined,
                current_step: 8
              });
               if (saved) goToView('smokingHabits');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'smokingHabits') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="smokingHabits" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--rose" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(244, 63, 94, 0.1)', color: '#f43f5e' }}>
            <FontAwesomeIcon icon={faSmoking} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Smoking Habits</h2>
            <p>Tobacco usage information for comprehensive health assessment</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/smoking.png" alt="Smoking Habits Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Tobacco Usage</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="smoking-habits-content">
              {/* Question 1: Are you currently consuming any form of tobacco? */}
              <div className="smoking-habit-question-group">
                <p className="question-text">Are you currently consuming any form of tobacco?</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="consumingTobacco"
                      value="yes"
                      checked={consumingTobacco === 'yes'}
                      onChange={() => setConsumingTobacco('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="consumingTobacco"
                      value="no"
                      checked={consumingTobacco === 'no'}
                      onChange={() => { setConsumingTobacco('no'); setTobaccoType(''); setBediCount(''); setSmokingDuration(''); setTriedQuitSmoking(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>

              {/* Conditional sub-questions when consuming tobacco */}
              {consumingTobacco === 'yes' && (
                <div className="conditional-subquestion-group" style={{ marginLeft: 24, marginTop: 16, paddingLeft: 16, borderLeft: '3px solid #f43f5e' }}>
                  <div className="smoking-habit-question-group">
                    <p className="question-text">What type of tobacco do you use?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'cigarette', label: 'Cigarette' },
                        { value: 'bedi', label: 'Bedi' },
                        { value: 'pan', label: 'Pan' },
                        { value: 'gutka', label: 'Gutka' },
                        { value: 'raw_tobacco', label: 'Raw Tobacco' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="tobaccoType" value={opt.value}
                            checked={tobaccoType === opt.value}
                            onChange={() => setTobaccoType(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {tobaccoType === 'bedi' && (
                    <div className="smoking-habit-question-group" style={{ marginLeft: 24, marginTop: 8 }}>
                      <label className="detail-input-label">Number of Bedi per day</label>
                      <input type="text" className="detail-text-input"
                        placeholder="e.g., 5, 10..."
                        value={bediCount}
                        onChange={(e) => setBediCount(e.target.value)} />
                    </div>
                  )}

                  <div className="smoking-habit-question-group">
                    <p className="question-text">How long have you been using tobacco?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'lt_1', label: 'Less than 1 year' },
                        { value: '1_5', label: '1-5 years' },
                        { value: '5_10', label: '5-10 years' },
                        { value: 'gt_10', label: 'More than 10 years' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="smokingDuration" value={opt.value}
                            checked={smokingDuration === opt.value}
                            onChange={() => setSmokingDuration(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="smoking-habit-question-group">
                    <p className="question-text">Have you tried to quit tobacco?</p>
                    <div className="radio-option-group">
                      <label className="custom-radio-label">
                        <input type="radio" name="triedQuitSmoking" value="yes"
                          checked={triedQuitSmoking === 'yes'}
                          onChange={() => setTriedQuitSmoking('yes')} />
                        <span className="radio-indicator"></span>
                        Yes
                      </label>
                      <label className="custom-radio-label">
                        <input type="radio" name="triedQuitSmoking" value="no"
                          checked={triedQuitSmoking === 'no'}
                          onChange={() => setTriedQuitSmoking('no')} />
                        <span className="radio-indicator"></span>
                        No
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Question 2: Have you consumed tobacco in the past? — only relevant when not currently using */}
              {consumingTobacco === 'no' && (
                <div className="smoking-habit-question-group">
                  <p className="question-text">Have you consumed tobacco in the past?</p>
                  <div className="radio-option-group">
                    <label className="custom-radio-label">
                      <input
                        type="radio"
                        name="quitHabit"
                        value="yes"
                        checked={quitHabit === 'yes'}
                        onChange={() => setQuitHabit('yes')}
                      />
                      <span className="radio-indicator"></span>
                      Yes
                    </label>
                    <label className="custom-radio-label">
                      <input
                        type="radio"
                        name="quitHabit"
                        value="no"
                        checked={quitHabit === 'no'}
                        onChange={() => { setQuitHabit('no'); setQuitSmokingWhen(''); }}
                      />
                      <span className="radio-indicator"></span>
                      No
                    </label>
                  </div>
                </div>
              )}

              {/* Conditional: When did you quit? */}
              {consumingTobacco === 'no' && quitHabit === 'yes' && (
                <div className="conditional-subquestion-group" style={{ marginLeft: 24, marginTop: 12, paddingLeft: 16, borderLeft: '3px solid #f43f5e' }}>
                  <div className="smoking-habit-question-group">
                    <p className="question-text">When did you quit?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'lt_1', label: 'Less than 1 year ago' },
                        { value: '1_3', label: '1-3 years ago' },
                        { value: '3_5', label: '3-5 years ago' },
                        { value: 'gt_5', label: 'More than 5 years ago' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="quitSmokingWhen" value={opt.value}
                            checked={quitSmokingWhen === opt.value}
                            onChange={() => setQuitSmokingWhen(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div> {/* End smoking-habits-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('drinkingHabits')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!consumingTobacco) {
                setValidationError('Please answer the tobacco consumption question');
                return;
              }
              if (consumingTobacco === 'yes') {
                if (!tobaccoType) {
                  setValidationError('Please select the type of tobacco you use');
                  return;
                }
                if (!smokingDuration) {
                  setValidationError('Please select how long you have been using tobacco');
                  return;
                }
                if (!triedQuitSmoking) {
                  setValidationError('Please answer the quit tobacco question');
                  return;
                }
              }
              if (consumingTobacco === 'no') {
                if (!quitHabit) {
                  setValidationError('Please answer the past tobacco consumption question');
                  return;
                }
                if (quitHabit === 'yes' && !quitSmokingWhen) {
                  setValidationError('Please select when you quit tobacco');
                  return;
                }
              }
              setValidationError('');
              const isCurrentTobaccoUser = consumingTobacco === 'yes';
              const saved = await saveStepProgress({
                tobacco_current: isCurrentTobaccoUser,
                tobacco_type: isCurrentTobaccoUser ? tobaccoType : undefined,
                tobacco_duration: isCurrentTobaccoUser ? smokingDuration : undefined,
                tobacco_planning_quit: isCurrentTobaccoUser ? triedQuitSmoking === 'yes' : undefined,
                tobacco_quit: isCurrentTobaccoUser ? false : quitHabit === 'yes',
                tobacco_quit_period: !isCurrentTobaccoUser && quitHabit === 'yes' ? quitSmokingWhen : undefined,
                current_step: 9
              });
               if (saved) goToView('hereditaryQuestions');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'hereditaryQuestions') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="hereditaryQuestions" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--indigo" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
            <FontAwesomeIcon icon={faDna} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Hereditary Questions</h2>
            <p>Family medical history and medication usage information</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/Hereditary.png" alt="Hereditary Questions Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Family Health History</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="hereditary-questions-content">
              {/* Question 1: Chronic/Long term illness */}
              <div className="hereditary-question-group">
                <p className="question-text">Has any of your immediate family members suffered or are Suffering from any Chronic / Long term illness. To get the list please select one of the family member below</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="familyIllness"
                      value="yes"
                      checked={familyIllness === 'yes'}
                      onChange={() => setFamilyIllness('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="familyIllness"
                      value="no"
                      checked={familyIllness === 'no'}
                      onChange={() => { setFamilyIllness('no'); setSelectedFamilyDependants({}); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>

                {familyIllness === 'yes' && (
                  <div className="dependant-selection-group" style={{ marginTop: 16, padding: '16px 20px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                    <p className="question-text" style={{ fontSize: 14, marginBottom: 12 }}>Please select the family member(s) who have the illness:</p>
                    {loadingDependants ? (
                      <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading dependants...</p>
                    ) : familyDependants.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: 13 }}>No dependants found. Please add dependants in your profile first.</p>
                    ) : (
                      <div className="radio-option-group">
                        {familyDependants.map((dep) => {
                          const isSelected = dep.id in selectedFamilyDependants;
                          return (
                            <div key={dep.id}>
                              <div className={`custom-radio-label${isSelected ? ' dep-checked' : ''}`} onClick={() => {
                                setSelectedFamilyDependants(prev => {
                                  const next = { ...prev };
                                  if (isSelected) {
                                    delete next[dep.id];
                                  } else {
                                    next[dep.id] = '';
                                  }
                                  return next;
                                });
                              }} style={{ cursor: 'pointer' }}>
                                <span className="radio-indicator" style={isSelected ? { borderColor: 'var(--ha-primary)', boxShadow: '0 0 0 3px rgba(6, 182, 212, 0.12)' } : {}}></span>
                                {dep.name}{dep.relationship_name && ` (${dep.relationship_name})`}
                              </div>
                              {isSelected && (
                                <div style={{ marginTop: 8, marginBottom: 4, paddingLeft: 32 }}>
                                  <select
                                    value={selectedFamilyDependants[dep.id] || ''}
                                    onChange={(e) => {
                                      setSelectedFamilyDependants(prev => ({
                                        ...prev,
                                        [dep.id]: e.target.value
                                      }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '100%',
                                      padding: '10px 14px',
                                      border: '1.5px solid #e2e8f0',
                                      borderRadius: 10,
                                      fontSize: '0.88rem',
                                      fontFamily: "'Nunito', sans-serif",
                                      outline: 'none',
                                      transition: 'border-color 0.2s',
                                      background: '#fff',
                                      color: '#000',
                                      cursor: 'pointer',
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)'; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                                  >
                                    <option value="" disabled style={{ color: '#000' }}>Select disease for {dep.name}</option>
                                    {Object.entries(hraChoices.family_disease_list || {
                                      heart_disease: 'Heart Disease',
                                      stroke: 'Stroke',
                                      hypertension: 'Hypertension',
                                      cancer: 'Cancer',
                                      depression: 'Depression',
                                      type2_diabetes: 'Type 2 Diabetes',
                                      arthritis: 'Arthritis',
                                      osteoporosis: 'Osteoporosis',
                                      asthma: 'Asthma',
                                      copd: 'Chronic Obstructive Pulmonary Disease (COPD)',
                                      kidney_disease: 'Chronic Kidney Disease',
                                      oral_disease: 'Oral Disease',
                                    }).map(([value, label]) => (
                                      <option key={value} value={value} style={{ color: '#000' }}>{String(label)}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Question 2: I see my physician for routine check-ups, health screenings, and disease prevention */}
              <div className="hereditary-question-group">
                <p className="question-text">I see my physician for routine check-ups, health screenings, and disease prevention</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="physicianCheckup"
                      value="six_months"
                      checked={physicianCheckup === 'six_months'}
                      onChange={() => setPhysicianCheckup('six_months')}
                    />
                    <span className="radio-indicator"></span>
                    Every six months
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="physicianCheckup"
                      value="yearly"
                      checked={physicianCheckup === 'yearly'}
                      onChange={() => setPhysicianCheckup('yearly')}
                    />
                    <span className="radio-indicator"></span>
                    Yearly once
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="physicianCheckup"
                      value="few_times"
                      checked={physicianCheckup === 'few_times'}
                      onChange={() => setPhysicianCheckup('few_times')}
                    />
                    <span className="radio-indicator"></span>
                    Have conducted it few times in my life
                  </label>
                </div>
              </div>
  
              {/* Question 3: Are you currently taking any medication for conditions like, diabetes/hypertension/Stroke/Asthma etc. */}
              <div className="hereditary-question-group">
                <p className="question-text">Are you currently taking any medication for conditions like, diabetes/hypertension/Stroke/Asthma etc.</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="currentlyTakingMedication"
                      value="yes"
                      checked={currentlyTakingMedication === 'yes'}
                      onChange={() => setCurrentlyTakingMedication('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="currentlyTakingMedication"
                      value="no"
                      checked={currentlyTakingMedication === 'no'}
                      onChange={() => setCurrentlyTakingMedication('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
  
              {/* Question 4: Have you stopped taking any medications without consulting the doctor */}
              <div className="hereditary-question-group">
                <p className="question-text">Have you stopped taking any medications without consulting the doctor</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="stoppedMedication"
                      value="yes"
                      checked={stoppedMedication === 'yes'}
                      onChange={() => setStoppedMedication('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="stoppedMedication"
                      value="no"
                      checked={stoppedMedication === 'no'}
                      onChange={() => setStoppedMedication('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
  
              {/* Question 5: Are you taking any other sources of medicine (Ayurveda/Unani/homeopathy) */}
              <div className="hereditary-question-group">
                <p className="question-text">Are you taking any other sources of medicine (Ayurveda/Unani/homeopathy)</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherMedicineSources"
                      value="yes"
                      checked={otherMedicineSources === 'yes'}
                      onChange={() => setOtherMedicineSources('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherMedicineSources"
                      value="no"
                      checked={otherMedicineSources === 'no'}
                      onChange={() => setOtherMedicineSources('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
            </div> {/* End hereditary-questions-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('smokingHabits')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!familyIllness) {
                setValidationError('Please answer the family illness question');
                return;
              }
              if (familyIllness === 'yes' && Object.keys(selectedFamilyDependants).length === 0 && familyDependants.length > 0) {
                setValidationError('Please select at least one family member with the illness');
                return;
              }
              if (familyIllness === 'yes' && Object.keys(selectedFamilyDependants).length > 0) {
                const missingDisease = Object.entries(selectedFamilyDependants).find(([, disease]) => !disease.trim());
                if (missingDisease) {
                  const dep = familyDependants.find(d => d.id === Number(missingDisease[0]));
                  setValidationError(`Please enter the disease name for ${dep?.name || 'the selected family member'}`);
                  return;
                }
              }
              if (!physicianCheckup) {
                setValidationError('Please select your checkup frequency');
                return;
              }
              if (!currentlyTakingMedication) {
                setValidationError('Please answer the medication question');
                return;
              }
              if (!stoppedMedication) {
                setValidationError('Please answer the stopped medication question');
                return;
              }
              if (!otherMedicineSources) {
                setValidationError('Please answer the alternative medicine question');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                family_chronic_illness: familyIllness === 'yes',
                family_illness_records: familyIllness === 'yes'
                  ? Object.entries(selectedFamilyDependants).map(([depId, disease]) => ({ dependant: Number(depId), disease }))
                  : [],
                checkup_frequency: physicianCheckup,
                taking_regular_meds: currentlyTakingMedication === 'yes',
                stopped_meds_without_doctor: stoppedMedication === 'yes',
                other_alt_medicine: otherMedicineSources === 'yes',
                current_step: 10
              });
               if (saved) goToView('bowelBladderHabits');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'bowelBladderHabits') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="bowelBladderHabits" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--amber" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
            <FontAwesomeIcon icon={faToilet} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Bowel Bladder Habits</h2>
            <p>Digestive and urinary health indicators</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/BowelBladder.png" alt="Bowel Bladder Habits Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Digestive Health</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="bowel-bladder-habits-content">
              {/* Question 1: Do you feel difficulty in passing urine */}
              <div className="bowel-bladder-question-group">
                <p className="question-text">Do you feel difficulty in passing urine</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="difficultyUrinating"
                      value="yes"
                      checked={difficultyUrinating === 'yes'}
                      onChange={() => setDifficultyUrinating('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="difficultyUrinating"
                      value="no"
                      checked={difficultyUrinating === 'no'}
                      onChange={() => { setDifficultyUrinating('no'); setUrineDifficultyType(''); }}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>

              {difficultyUrinating === 'yes' && (
                <div className="conditional-subquestion-group" style={{ marginLeft: 24, marginTop: 12, paddingLeft: 16, borderLeft: '3px solid #f59e0b' }}>
                  <div className="bowel-bladder-question-group">
                    <p className="question-text">What kind of difficulty?</p>
                    <div className="radio-option-group">
                      {[
                        { value: 'flow_difficulty', label: 'Difficulty in flow of Urine' },
                        { value: 'pain_while_urinating', label: 'Pain while Urination' },
                        { value: 'blood_urination', label: 'Blood Tinged Urination' },
                      ].map(opt => (
                        <label key={opt.value} className="custom-radio-label">
                          <input type="radio" name="urineDifficultyType" value={opt.value}
                            checked={urineDifficultyType === opt.value}
                            onChange={() => setUrineDifficultyType(opt.value)} />
                          <span className="radio-indicator"></span>
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Question 2: Do you feel difficulty in passing stools */}
              <div className="bowel-bladder-question-group">
                <p className="question-text">Do you feel difficulty in passing stools</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="difficultyStooling"
                      value="yes"
                      checked={difficultyStooling === 'yes'}
                      onChange={() => setDifficultyStooling('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="difficultyStooling"
                      value="no"
                      checked={difficultyStooling === 'no'}
                      onChange={() => setDifficultyStooling('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
            </div> {/* End bowel-bladder-habits-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('hereditaryQuestions')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!difficultyUrinating) {
                setValidationError('Please answer the urination question');
                return;
              }
              if (difficultyUrinating === 'yes' && !urineDifficultyType) {
                setValidationError('Please select the type of urination difficulty');
                return;
              }
              if (!difficultyStooling) {
                setValidationError('Please answer the stool question');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                difficulty_urine: difficultyUrinating === 'yes',
                difficulty_urine_reasons: difficultyUrinating === 'yes' ? urineDifficultyType : undefined,
                difficulty_stools: difficultyStooling === 'yes',
                current_step: 11
              });
               if (saved) goToView('fitnessProfile');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'fitnessProfile') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="fitnessProfile" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--green" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <FontAwesomeIcon icon={faRunning} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Fitness Profile</h2>
            <p>Your physical activity levels and exercise routine</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/Fitness-Profile.png" alt="Fitness Profile Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Physical Activity</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="fitness-profile-content">
              {/* Question 1: Stretching/Warm up */}
              <div className="fitness-question-group">
                <p className="question-text">Stretching/Warm up</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="stretching"
                      value="lt_30"
                      checked={stretching === 'lt_30'}
                      onChange={() => setStretching('lt_30')}
                    />
                    <span className="radio-indicator"></span>
                    Less than 30 mins
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="stretching"
                      value="30_60"
                      checked={stretching === '30_60'}
                      onChange={() => setStretching('30_60')}
                    />
                    <span className="radio-indicator"></span>
                    30 mins - 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="stretching"
                      value="gt_60"
                      checked={stretching === 'gt_60'}
                      onChange={() => setStretching('gt_60')}
                    />
                    <span className="radio-indicator"></span>
                    More than 1 hr
                  </label>
                </div>
              </div>
  
              {/* Question 2: Aerobic Activity */}
              <div className="fitness-question-group">
                <p className="question-text">Aerobic Activity</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="aerobicActivity"
                      value="lt_30"
                      checked={aerobicActivity === 'lt_30'}
                      onChange={() => setAerobicActivity('lt_30')}
                    />
                    <span className="radio-indicator"></span>
                    Less than 30 mins
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="aerobicActivity"
                      value="30_60"
                      checked={aerobicActivity === '30_60'}
                      onChange={() => setAerobicActivity('30_60')}
                    />
                    <span className="radio-indicator"></span>
                    30 mins - 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="aerobicActivity"
                      value="gt_60"
                      checked={aerobicActivity === 'gt_60'}
                      onChange={() => setAerobicActivity('gt_60')}
                    />
                    <span className="radio-indicator"></span>
                    More than 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="aerobicActivity"
                      value="none"
                      checked={aerobicActivity === 'none'}
                      onChange={() => setAerobicActivity('none')}
                    />
                    <span className="radio-indicator"></span>
                    Not Really
                  </label>
                </div>
              </div>
  
              {/* Question 3: Strength and Conditioning Training */}
              <div className="fitness-question-group">
                <p className="question-text">Strength and Conditioning Training</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="strengthTraining"
                      value="lt_30"
                      checked={strengthTraining === 'lt_30'}
                      onChange={() => setStrengthTraining('lt_30')}
                    />
                    <span className="radio-indicator"></span>
                    Less than 30 mins
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="strengthTraining"
                      value="30_60"
                      checked={strengthTraining === '30_60'}
                      onChange={() => setStrengthTraining('30_60')}
                    />
                    <span className="radio-indicator"></span>
                    30 mins - 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="strengthTraining"
                      value="gt_60"
                      checked={strengthTraining === 'gt_60'}
                      onChange={() => setStrengthTraining('gt_60')}
                    />
                    <span className="radio-indicator"></span>
                    More than 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="strengthTraining"
                      value="none"
                      checked={strengthTraining === 'none'}
                      onChange={() => setStrengthTraining('none')}
                    />
                    <span className="radio-indicator"></span>
                    Not Really
                  </label>
                </div>
              </div>
  
              {/* Question 4: Walking */}
              <div className="fitness-question-group">
                <p className="question-text">Walking</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="walking"
                      value="lt_30"
                      checked={walking === 'lt_30'}
                      onChange={() => setWalking('lt_30')}
                    />
                    <span className="radio-indicator"></span>
                    Less than 30 mins
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="walking"
                      value="30_60"
                      checked={walking === '30_60'}
                      onChange={() => setWalking('30_60')}
                    />
                    <span className="radio-indicator"></span>
                    30 mins - 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="walking"
                      value="gt_60"
                      checked={walking === 'gt_60'}
                      onChange={() => setWalking('gt_60')}
                    />
                    <span className="radio-indicator"></span>
                    More than 1 hr
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="walking"
                      value="none"
                      checked={walking === 'none'}
                      onChange={() => setWalking('none')}
                    />
                    <span className="radio-indicator"></span>
                    Not Really
                  </label>
                </div>
              </div>
  
              {/* Question 5: Any other physical activity (Other than Walking) */}
              <div className="fitness-question-group">
                <p className="question-text">Any other physical activity (Other than Walking)</p>
                <div className="radio-option-group horizontal-options">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherActivity"
                      value="running"
                      checked={otherActivity === 'running'}
                      onChange={() => { setOtherActivity('running'); setOtherActivityText(''); }}
                    />
                    <span className="radio-indicator"></span>
                    Running
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherActivity"
                      value="sports"
                      checked={otherActivity === 'sports'}
                      onChange={() => { setOtherActivity('sports'); setOtherActivityText(''); }}
                    />
                    <span className="radio-indicator"></span>
                    Playing Any sport
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherActivity"
                      value="cycling"
                      checked={otherActivity === 'cycling'}
                      onChange={() => { setOtherActivity('cycling'); setOtherActivityText(''); }}
                    />
                    <span className="radio-indicator"></span>
                    Cycling
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherActivity"
                      value="gymnastics"
                      checked={otherActivity === 'gymnastics'}
                      onChange={() => { setOtherActivity('gymnastics'); setOtherActivityText(''); }}
                    />
                    <span className="radio-indicator"></span>
                    Gymnastics
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="otherActivity"
                      value="other"
                      checked={otherActivity === 'other'}
                      onChange={() => setOtherActivity('other')}
                    />
                    <span className="radio-indicator"></span>
                    Other
                  </label>
                </div>
                {otherActivity === 'other' && (
                  <div className="conditional-detail-input" style={{ marginTop: 12, marginLeft: 24 }}>
                    <label className="detail-input-label">Please specify the activity</label>
                    <input type="text" className="detail-text-input"
                      placeholder="e.g., Swimming, Yoga, Martial Arts..."
                      maxLength={255}
                      value={otherActivityText}
                      onChange={(e) => setOtherActivityText(e.target.value)} />
                  </div>
                )}
              </div>
            </div> {/* End fitness-profile-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('bowelBladderHabits')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!stretching) {
                setValidationError('Please select your stretching duration');
                return;
              }
              if (!aerobicActivity) {
                setValidationError('Please select your aerobic activity duration');
                return;
              }
              if (!strengthTraining) {
                setValidationError('Please select your strength training duration');
                return;
              }
              if (!walking) {
                setValidationError('Please select your walking duration');
                return;
              }
              if (!otherActivity) {
                setValidationError('Please select your other activity level');
                return;
              }
              if (otherActivity === 'other' && !otherActivityText.trim()) {
                setValidationError('Please specify the activity');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                stretch_duration: stretching,
                cardio_duration: aerobicActivity,
                strength_duration: strengthTraining,
                walking_duration: walking,
                other_activity: otherActivity,
                other_activity_text: otherActivity === 'other' ? otherActivityText.trim() : '',
                current_step: 12
              });
               if (saved) goToView('mentalWellness');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'mentalWellness') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="mentalWellness" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--blue" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <FontAwesomeIcon icon={faBrain} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Mental Wellness</h2>
            <p>Emotional and psychological well-being assessment</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/Mental-wellness.png" alt="Mental Wellness Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Emotional Health</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="mental-wellness-content">
              {/* Question 1: Do you have little interest in doing things that you enjoyed before */}
              <div className="mental-wellness-question-group">
                <p className="question-text">Do you have little interest in doing things that you enjoyed before</p>
                <div className="radio-option-group">
                  {FREQ5_OPTIONS.map((opt) => (
                    <label className="custom-radio-label" key={opt.value}>
                      <input
                        type="radio"
                        name="littleInterest"
                        value={opt.value}
                        checked={littleInterest === opt.value}
                        onChange={() => setLittleInterest(opt.value)}
                      />
                      <span className="radio-indicator"></span>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
  
              {/* Question 2: Have you been feeling sad or depressed most of the times */}
              <div className="mental-wellness-question-group">
                <p className="question-text">Have you been feeling sad or depressed most of the times</p>
                <div className="radio-option-group">
                  {FREQ5_OPTIONS.map((opt) => (
                    <label className="custom-radio-label" key={opt.value}>
                      <input
                        type="radio"
                        name="feelingSad"
                        value={opt.value}
                        checked={feelingSad === opt.value}
                        onChange={() => setFeelingSad(opt.value)}
                      />
                      <span className="radio-indicator"></span>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
  
              {/* Question 3: Do you have problems in sleep and appetite */}
              <div className="mental-wellness-question-group">
                <p className="question-text">Do you have problems in sleep and appetite</p>
                <div className="radio-option-group">
                  {FREQ5_OPTIONS.map((opt) => (
                    <label className="custom-radio-label" key={opt.value}>
                      <input
                        type="radio"
                        name="sleepAppetiteProblems"
                        value={opt.value}
                        checked={sleepAppetiteProblems === opt.value}
                        onChange={() => setSleepAppetiteProblems(opt.value)}
                      />
                      <span className="radio-indicator"></span>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
  
              {/* Question 4: Have you been tired and having very little energy */}
              <div className="mental-wellness-question-group">
                <p className="question-text">Have you been tired and having very little energy</p>
                <div className="radio-option-group">
                  {FREQ5_OPTIONS.map((opt) => (
                    <label className="custom-radio-label" key={opt.value}>
                      <input
                        type="radio"
                        name="lowEnergy"
                        value={opt.value}
                        checked={lowEnergy === opt.value}
                        onChange={() => setLowEnergy(opt.value)}
                      />
                      <span className="radio-indicator"></span>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
  
              {/* Question 5: Have you been Anxious / Restless or having multiple worries and doubts in mind than usual */}
              <div className="mental-wellness-question-group">
                <p className="question-text">Have you been Anxious / Restless or having multiple worries and doubts in mind than usual</p>
                <div className="radio-option-group">
                  {FREQ5_OPTIONS.map((opt) => (
                    <label className="custom-radio-label" key={opt.value}>
                      <input
                        type="radio"
                        name="anxiousRestless"
                        value={opt.value}
                        checked={anxiousRestless === opt.value}
                        onChange={() => setAnxiousRestless(opt.value)}
                      />
                      <span className="radio-indicator"></span>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </div> {/* End mental-wellness-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('fitnessProfile')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={async () => {
              if (!littleInterest) {
                setValidationError('Please answer the interest/pleasure question');
                return;
              }
              if (!feelingSad) {
                setValidationError('Please answer the feeling sad question');
                return;
              }
              if (!sleepAppetiteProblems) {
                setValidationError('Please answer the sleep/appetite question');
                return;
              }
              if (!lowEnergy) {
                setValidationError('Please answer the energy level question');
                return;
              }
              if (!anxiousRestless) {
                setValidationError('Please answer the anxiety question');
                return;
              }
              setValidationError('');
              const saved = await saveStepProgress({
                low_interest: littleInterest,
                depressed: feelingSad,
                sleep_appetite_issue: sleepAppetiteProblems,
                low_energy: lowEnergy,
                anxious: anxiousRestless,
                current_step: 13
              });
               if (saved) goToView('employeeWellness');
            }}
          >
            {savingProgress ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }
  if (view === 'employeeWellness') {
    return (
      <div className="assessment-records-page">
        <StepProgressBar currentStep="employeeWellness" onConfirmExit={handleExitAssessment} />
        <div className="ha-form-section-header ha-form-section-header--teal" style={{ maxWidth: 1200, margin: '0 auto 20px' }}>
          <div className="ha-form-section-header-icon" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
            <FontAwesomeIcon icon={faBriefcase} />
          </div>
          <div className="ha-form-section-header-text">
            <h2>Employee Wellness</h2>
            <p>Work-life balance and occupational stress assessment</p>
          </div>
        </div>
        <div className="assessment-records-container">
          <div className="assessment-illustration">
            <img src="/employ-wellnes.png" alt="Employee Wellness Illustration" />
          </div>
          <div className="assessment-records-panel">
            <div className="panel-header">
              <h2>Work Stress Assessment</h2>
              <p>Please enter correct information for us to provide accurate output</p>
            </div>
  
            <div className="employee-wellness-content">
              {/* Question 1: Do you feel stress from work is affecting your personal life? */}
              <div className="employee-wellness-question-group">
                <p className="question-text">Do you feel stress from work is affecting your personal life?</p>
                <div className="radio-option-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="workStress"
                      value="yes"
                      checked={workStress === 'yes'}
                      onChange={() => setWorkStress('yes')}
                    />
                    <span className="radio-indicator"></span>
                    Yes
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="workStress"
                      value="no"
                      checked={workStress === 'no'}
                      onChange={() => setWorkStress('no')}
                    />
                    <span className="radio-indicator"></span>
                    No
                  </label>
                </div>
              </div>
              {workStress === 'yes' && (
  <div className="employee-wellness-question-group">
    <p className="question-text">What's stressing you out?</p>
    <div className="checkbox-option-group">
      {Object.entries(hraChoices.work_stress_reasons || {
        // Fallback if the /choices call hasn't returned yet.
        increased_workload: 'Increased Workload',
        long_working_hours: 'Long Working Hours',
        health_issues: 'Any Health Issues',
        financial_issue: 'Financial Issues',
        others: 'Others',
      }).map(([value, label]) => ({ value, label: String(label) })).map((option) => (
        <label key={option.value} className="custom-checkbox-label">
          <input
            type="checkbox"
            value={option.value}
            checked={stressReasons.includes(option.value)}
            onChange={(e) => {
              const updated = e.target.checked
                ? [...stressReasons, option.value]
                : stressReasons.filter((item) => item !== option.value);
              setStressReasons(updated);
            }}
          />
          <span className="checkbox-indicator"></span>
          {option.label}
        </label>
      ))}
    </div>
    {stressReasons.includes('others') && (
      <div className="conditional-detail-input" style={{ marginTop: 12, marginLeft: 24 }}>
        <label className="detail-input-label">Please specify</label>
        <input type="text" className="detail-text-input"
          placeholder="Describe your stress reason..."
          value={stressOtherText}
          onChange={(e) => setStressOtherText(e.target.value)} />
      </div>
    )}
  </div>
)}

            </div> {/* End employee-wellness-content */}
          </div> {/* End assessment-records-panel */}
        </div> {/* End assessment-records-container */}

        {validationError && (
          <div className="ha-validation-error">
            <p>{validationError}</p>
          </div>
        )}
        <div className="page-navigation-buttons">
          <button className="action-btn prev-btn" onClick={() => goToView('mentalWellness')}>Previous</button>
          <button
            className="action-btn next-btn"
            disabled={savingProgress}
            onClick={() => {
              if (!workStress) {
                setValidationError('Please answer the work stress question');
                return;
              }
              setValidationError('');
              setShowSubmitConfirm(true);
            }}
          >
            {savingProgress ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {showSubmitConfirm && (
          <div className="popup-overlay">
            <div className="popup-content">
              <button className="popup-close" onClick={() => setShowSubmitConfirm(false)} aria-label="Close">×</button>
              <h3>Submit your health assessment?</h3>
              <p style={{ marginTop: -16, marginBottom: 24, color: '#64748b', fontSize: '0.9rem' }}>
                Once submitted, your risk score and report will be generated and the assessment will be marked as completed.
              </p>
              <div className="popup-actions">
                <button
                  className="popup-btn"
                  disabled={savingProgress}
                  onClick={async () => {
                    setShowSubmitConfirm(false);
                     const saved = await saveStepProgress({
                       work_stress_affecting_life: workStress === 'yes',
                       work_stress_reasons: stressReasons.length > 0 ? stressReasons : undefined,
                       current_step: 13,
                     });
                     if (saved) await submitAssessment();
                  }}
                >
                  {savingProgress ? 'Submitting...' : 'Yes, Submit'}
                </button>
                <button
                  className="popup-btn"
                  style={{ background: '#e2e8f0', color: '#334155' }}
                  onClick={() => setShowSubmitConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (view === 'submitted') {
    return (
      <div className="ha-congrats-page">
        <div className="ha-congrats-card">
          <div className="ha-congrats-checkmark">
            <svg viewBox="0 0 52 52" className="ha-congrats-check-svg">
              <circle className="ha-congrats-circle" cx="26" cy="26" r="25" fill="none" />
              <path className="ha-congrats-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
          <h2 className="ha-congrats-title">Congratulations!</h2>
          <p className="ha-congrats-subtitle">You have successfully completed your health assessment.</p>
        </div>

        {canAccess('disease-prediction') && diseasePrediction && (() => {
          const cat = diseasePrediction.overall_risk_category;
          const colorMap: Record<string, string> = { low: '#10b981', moderate: '#f59e0b', high: '#f97316', critical: '#ef4444' };
          const bgMap: Record<string, string> = { low: '#ecfdf5', moderate: '#fffbeb', high: '#fff7ed', critical: '#fef2f2' };
          const accentColor = colorMap[cat] || '#f59e0b';
          const diseases = diseasePrediction.disease_details
            ? Object.entries(diseasePrediction.disease_details).sort(([, a]: [string, any], [, b]: [string, any]) => b.score - a.score)
            : [];
          return (
            <div className="ha-disease-prediction-card" onClick={() => navigate('/disease-prediction')} style={{ cursor: 'pointer' }}>
              <div className="ha-dp-header">
                <h3 className="ha-dp-title">Disease Risk Prediction</h3>
                <span className="ha-dp-view-link">View Full Report &rarr;</span>
              </div>
              <div className="ha-dp-overview">
                <div className="ha-dp-score-circle" style={{ background: accentColor, boxShadow: `0 4px 14px ${accentColor}40` }}>
                  <span className="ha-dp-score-num">{diseasePrediction.overall_risk_score}</span>
                  <span className="ha-dp-score-label">Score</span>
                </div>
                <div className="ha-dp-overview-info">
                  <span className="ha-dp-category-badge" style={{ background: bgMap[cat] || bgMap.moderate, color: accentColor }}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)} Risk
                  </span>
                  <span className="ha-dp-method">Method: {diseasePrediction.prediction_method === 'rule_based' ? 'Rule-Based' : 'ML Model'}</span>
                </div>
              </div>
              {diseases.length > 0 && (
                <div className="ha-dp-bars">
                  {diseases.slice(0, 5).map(([name, d]: [string, any]) => (
                    <div className="ha-dp-bar-item" key={name}>
                      <div className="ha-dp-bar-label">
                        <span>{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                        <span style={{ color: colorMap[d.category] || '#94a3b8' }}>{d.score}</span>
                      </div>
                      <div className="ha-dp-bar-track">
                        <div className="ha-dp-bar-fill" style={{ width: `${Math.min(d.score, 100)}%`, background: colorMap[d.category] || '#94a3b8' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {currentAssessmentId && (
          <HealthAssessmentReport
            assessmentId={currentAssessmentId}
            reportData={submittedReportData ?? undefined}
            onBackToAssessments={() => setView('initial')}
          />
        )}
      </div>
    );
  }
  if (view === 'viewReport') {
    return (
      <div className="ha-congrats-page">
        {canAccess('disease-prediction') && diseasePrediction && (() => {
          const cat = diseasePrediction.overall_risk_category;
          const colorMap: Record<string, string> = { low: '#10b981', moderate: '#f59e0b', high: '#f97316', critical: '#ef4444' };
          const bgMap: Record<string, string> = { low: '#ecfdf5', moderate: '#fffbeb', high: '#fff7ed', critical: '#fef2f2' };
          const accentColor = colorMap[cat] || '#f59e0b';
          const diseases = diseasePrediction.disease_details
            ? Object.entries(diseasePrediction.disease_details).sort(([, a]: [string, any], [, b]: [string, any]) => b.score - a.score)
            : [];
          return (
            <div className="ha-disease-prediction-card" onClick={() => navigate('/disease-prediction')} style={{ cursor: 'pointer' }}>
              <div className="ha-dp-header">
                <h3 className="ha-dp-title">Disease Risk Prediction</h3>
                <span className="ha-dp-view-link">View Full Report &rarr;</span>
              </div>
              <div className="ha-dp-overview">
                <div className="ha-dp-score-circle" style={{ background: accentColor, boxShadow: `0 4px 14px ${accentColor}40` }}>
                  <span className="ha-dp-score-num">{diseasePrediction.overall_risk_score}</span>
                  <span className="ha-dp-score-label">Score</span>
                </div>
                <div className="ha-dp-overview-info">
                  <span className="ha-dp-category-badge" style={{ background: bgMap[cat] || bgMap.moderate, color: accentColor }}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)} Risk
                  </span>
                  <span className="ha-dp-method">Method: {diseasePrediction.prediction_method === 'rule_based' ? 'Rule-Based' : 'ML Model'}</span>
                </div>
              </div>
              {diseases.length > 0 && (
                <div className="ha-dp-bars">
                  {diseases.slice(0, 5).map(([name, d]: [string, any]) => (
                    <div className="ha-dp-bar-item" key={name}>
                      <div className="ha-dp-bar-label">
                        <span>{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                        <span style={{ color: colorMap[d.category] || '#94a3b8' }}>{d.score}</span>
                      </div>
                      <div className="ha-dp-bar-track">
                        <div className="ha-dp-bar-fill" style={{ width: `${Math.min(d.score, 100)}%`, background: colorMap[d.category] || '#94a3b8' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {currentAssessmentId && (
          <HealthAssessmentReport
            assessmentId={currentAssessmentId}
            reportData={submittedReportData ?? undefined}
            onBackToAssessments={() => setView('initial')}
          />
        )}
      </div>
    );
  }

{/* basic section ends */}
  return (
    <div className="ha-page">
      {/* ============ WELCOME BANNER (matching HR) ============ */}
      <section className="ha-welcome-banner">
        <Container>
          <BackButton />
          <div className="ha-welcome-layout">
            <div className="ha-welcome-text">
              <h1 className="ha-welcome-title">
                <FontAwesomeIcon icon={faClipboardCheck} />
                Health Assessment
              </h1>
              <p className="ha-welcome-subtitle">
                Evaluate your overall health with our comprehensive assessment tool. Get personalized insights and recommendations.
              </p>
            </div>
            <div className="ha-welcome-actions">
              <button className="ha-welcome-btn primary" onClick={handleStartNewAssessment} disabled={savingProgress}>
                <FontAwesomeIcon icon={faFileMedical} />
                {savingProgress ? 'Starting...' : 'New Evaluation'}
              </button>
              <button className="ha-welcome-btn outline" onClick={async () => {
                const inProgress = assessmentRecords.find(r => r.status === 'in_process');
                if (inProgress) {
                  await resumeAssessment(inProgress);
                } else {
                  handleStartNewAssessment();
                }
              }}>
                <FontAwesomeIcon icon={faPlayCircle} />
                Resume
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ STATS ROW (matching HR) ============ */}
      <section className="ha-stats-section">
        <Container>
          <div className="ha-stats-row">
            <div className="ha-stat-card">
              <div className="ha-stat-icon" style={{ background: 'rgba(6, 182, 212, 0.08)', color: '#06b6d4' }}>
                <FontAwesomeIcon icon={faClipboardCheck} />
              </div>
              <div className="ha-stat-info">
                <span className="ha-stat-value">{assessmentRecords.length}</span>
                <span className="ha-stat-label">Total Assessments</span>
              </div>
            </div>
            <div className="ha-stat-card">
              <div className="ha-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#10b981' }}>
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <div className="ha-stat-info">
                <span className="ha-stat-value">{assessmentRecords.filter(r => r.status === 'active').length}</span>
                <span className="ha-stat-label">Completed</span>
              </div>
            </div>
            <div className="ha-stat-card">
              <div className="ha-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.08)', color: '#f59e0b' }}>
                <FontAwesomeIcon icon={faPlayCircle} />
              </div>
              <div className="ha-stat-info">
                <span className="ha-stat-value">{assessmentRecords.filter(r => r.status === 'in_process').length}</span>
                <span className="ha-stat-label">In Progress</span>
              </div>
            </div>
            <div className="ha-stat-card">
              <div className="ha-stat-icon" style={{ background: 'rgba(139, 92, 246, 0.08)', color: '#8b5cf6' }}>
                <FontAwesomeIcon icon={faFileMedical} />
              </div>
              <div className="ha-stat-info">
                <span className="ha-stat-value">13</span>
                <span className="ha-stat-label">Health Areas</span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ PAST ASSESSMENTS ============ */}
      <Container>
        <div className="ha-past-assessments-section">
          <div className="ha-section-header">
            <div className="ha-section-header-text">
              <h2>
                <FontAwesomeIcon icon={faListCheck} className="ha-section-header-icon" />
                Past Assessments
              </h2>
              <p>View and manage your previous health assessment reports</p>
            </div>
          </div>
          {loadingRecords ? (
            <p style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading assessments...</p>
          ) : assessmentRecords.length === 0 ? (
            <p style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
              No health assessments yet. Start your first evaluation above.
            </p>
          ) : (
            <div className="assessment-carousel-wrapper">
              {assessmentRecords.length > RECORDS_VISIBLE && (
                <button className="carousel-arrow prev" onClick={handlePrev}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}
              <div className="assessment-carousel">
                {getVisibleRecords().map((record, index) => (
                  <div className="assessment-card" key={record.id || index}>
                    <div className="assessment-card-header">
                      <div>
                        <span className="record-name">
                          {typeof record.for_whom === 'object' ? record.for_whom.name : (record.dependant_data?.name || 'Self')}
                        </span>
                        <span className="record-relation">
                          {typeof record.for_whom === 'object' ? record.for_whom.type : (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                        </span>
                      </div>
                      {record.risk_category && (
                        <span className={`assessment-card-risk-badge risk-${(record.risk_category || '').toLowerCase()}`}>
                          {(record.risk_category || '').charAt(0).toUpperCase() + (record.risk_category || '').slice(1)}
                        </span>
                      )}
                    </div>
                    <div className="assessment-card-info">
                      <p><strong>Created On:</strong> {new Date(record.created_at).toLocaleString()}</p>
                      <p><strong>Status:</strong> {record.status === 'active' ? 'Completed' : 'In Progress'}</p>
                    </div>
                    <div className="assessment-card-actions">
                      {record.status === 'active' ? (
                      <button
                        className="download-btn"
                        onClick={() => viewAssessmentReport(record)}
                      >
                        <FontAwesomeIcon icon={faEye} /> View Assessment
                      </button>
                      ) : (
                      <button
                        className="download-btn resume-btn"
                        onClick={() => resumeAssessment(record)}
                      >
                        <FontAwesomeIcon icon={faPlayCircle} /> Resume
                      </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {assessmentRecords.length > RECORDS_VISIBLE && (
                <button className="carousel-arrow next" onClick={handleNext}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              )}
            </div>
          )}

          {/* Health Analytics Section */}
          {(() => {
            const completedAssessments = assessmentRecords
              .filter(r => r.status === 'active')
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

            if (completedAssessments.length === 0) return null;

            const latestAssessment = completedAssessments[completedAssessments.length - 1];
            const previousAssessment = completedAssessments.length >= 2
              ? completedAssessments[completedAssessments.length - 2]
              : null;

            // Wellness Score (0-100, higher = healthier) to match the report.
            const toWellness = (t: number | null | undefined) =>
              t == null ? 0 : Math.max(0, Math.min(100, 100 - t));

            const latestScore = toWellness(latestAssessment.total_score);
            const previousScore = previousAssessment
              ? toWellness(previousAssessment.total_score)
              : null;
            const scoreDiff = previousScore !== null ? latestScore - previousScore : null;

            const riskCounts = completedAssessments.reduce((acc, r) => {
              const cat = (r.risk_category || 'low').toLowerCase();
              acc[cat] = (acc[cat] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);

            const RISK_COLORS: Record<string, string> = {
              low: '#10b981',
              medium: '#f59e0b',
              high: '#ef4444',
            };

            const pieData = Object.entries(riskCounts).map(([name, value]) => ({
              name: name.charAt(0).toUpperCase() + name.slice(1),
              value,
              color: RISK_COLORS[name] || '#94a3b8',
            }));

            const trendData = completedAssessments.map(r => {
              const d = new Date(r.created_at);
              return {
                date: `${d.getDate().toString().padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`,
                score: toWellness(r.total_score),
                risk: r.risk_category || 'N/A',
                fullDate: d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
              };
            });

            const latestRisk = (latestAssessment.risk_category || 'low').toLowerCase();

            return (
              <div className="ha-analytics-section">
                <div className="ha-analytics-header">
                  <div className="ha-analytics-header-icon">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div>
                    <h3 className="ha-analytics-title">Your Health Analytics</h3>
                    <p className="ha-analytics-subtitle">Insights from your completed assessments</p>
                  </div>
                </div>

                <div className="ha-analytics-grid">
                  {/* 1. Wellness Score Trend */}
                  <div className="ha-analytics-card ha-analytics-card--trend">
                    <h4 className="ha-analytics-card-title">Wellness Score Trend</h4>
                    <p className="ha-analytics-card-hint">Higher is healthier &middot; out of 100</p>
                    <div className="ha-analytics-chart-wrap">
                      {completedAssessments.length === 1 ? (
                        <div className="ha-analytics-single-score">
                          <div className="ha-analytics-single-score-value">{latestScore}</div>
                          <div className="ha-analytics-single-score-label">Wellness Score</div>
                          <div className="ha-analytics-single-score-date">{trendData[0]?.fullDate}</div>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                            <defs>
                              <linearGradient id="scoreGradient2" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#06b6d4" />
                                <stop offset="100%" stopColor="#8b5cf6" />
                              </linearGradient>
                            </defs>
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              axisLine={{ stroke: '#e2e8f0' }}
                              tickLine={false}
                            />
                            <YAxis
                              domain={[0, 100]}
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              axisLine={false}
                              tickLine={false}
                              width={35}
                            />
                            <Tooltip
                              contentStyle={{
                                background: '#ffffff',
                                border: 'none',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                padding: '10px 14px',
                                fontFamily: 'Nunito, sans-serif',
                                fontSize: '13px',
                              }}
                              formatter={(value: any, _name: any, props: any) => {
                                const risk = props.payload?.risk || '';
                                return [`Wellness: ${value}/100  |  Risk: ${risk}`, ''];
                              }}
                              labelFormatter={(label: any) => label}
                            />
                            <Line
                              type="monotone"
                              dataKey="score"
                              stroke="url(#scoreGradient2)"
                              strokeWidth={3}
                              dot={{ r: 5, fill: '#06b6d4', stroke: '#ffffff', strokeWidth: 2 }}
                              activeDot={{ r: 7, fill: '#8b5cf6', stroke: '#ffffff', strokeWidth: 2 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  {/* 2. Risk Level Summary (Donut) */}
                  <div className="ha-analytics-card ha-analytics-card--donut">
                    <h4 className="ha-analytics-card-title">Risk Distribution</h4>
                    <p className="ha-analytics-card-hint">How your assessments break down by risk level</p>
                    <div className="ha-analytics-chart-wrap ha-analytics-donut-wrap">
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={75}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {pieData.map((entry, idx) => (
                              <Cell key={idx} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              background: '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                              padding: '8px 14px',
                              fontFamily: 'Nunito, sans-serif',
                              fontSize: '13px',
                            }}
                            formatter={(value: any, name: any) => [`${value} assessment${value > 1 ? 's' : ''}`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="ha-analytics-donut-center">
                        <span className="ha-analytics-donut-center-label">Latest</span>
                        <span
                          className="ha-analytics-donut-center-value"
                          style={{ color: RISK_COLORS[latestRisk] || '#64748b' }}
                        >
                          {latestRisk.charAt(0).toUpperCase() + latestRisk.slice(1)}
                        </span>
                      </div>
                      <div className="ha-analytics-legend">
                        {pieData.map((entry, idx) => (
                          <div className="ha-analytics-legend-item" key={idx}>
                            <span className="ha-analytics-legend-dot" style={{ background: entry.color }} />
                            <span className="ha-analytics-legend-text">{entry.name} ({entry.value})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 3. Score Comparison Card */}
                  <div className="ha-analytics-card ha-analytics-card--comparison">
                    <h4 className="ha-analytics-card-title">Latest Wellness Score</h4>
                    <p className="ha-analytics-card-hint">Same score you see on the assessment report</p>
                    <div className="ha-analytics-comparison-content">
                      <div className="ha-analytics-big-score">{latestScore}</div>
                      <div className="ha-analytics-score-label">out of 100 &middot; higher is better</div>

                      {scoreDiff !== null && (
                        <div className={`ha-analytics-score-change ${scoreDiff >= 0 ? 'improved' : 'worsened'}`}>
                          <FontAwesomeIcon icon={scoreDiff >= 0 ? faArrowUp : faArrowDown} />
                          <span>{scoreDiff > 0 ? '+' : ''}{scoreDiff} pts</span>
                          <span className="ha-analytics-change-note">vs previous</span>
                        </div>
                      )}

                      <div
                        className="ha-analytics-risk-badge"
                        style={{
                          background: `${RISK_COLORS[latestRisk] || '#94a3b8'}18`,
                          color: RISK_COLORS[latestRisk] || '#64748b',
                          border: `1.5px solid ${RISK_COLORS[latestRisk] || '#94a3b8'}40`,
                        }}
                      >
                        {latestRisk.charAt(0).toUpperCase() + latestRisk.slice(1)} Risk
                      </div>

                      <div className="ha-analytics-total-count">
                        <span className="ha-analytics-total-count-number">{completedAssessments.length}</span>
                        <span className="ha-analytics-total-count-label">Assessment{completedAssessments.length > 1 ? 's' : ''} Completed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

        </div>
      </Container>

      {/* In-app toast notification (replaces browser alert) */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            animation: 'fadeIn 0.3s ease',
            background: toastMsg.type === 'success' ? '#10b981' : '#ef4444',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}

      {showNewAssessmentConfirm && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="popup-close" onClick={() => setShowNewAssessmentConfirm(false)} aria-label="Close">×</button>
            <h3>Start a new assessment?</h3>
            <p style={{ marginTop: -16, marginBottom: 24, color: '#64748b', fontSize: '0.9rem' }}>
              You already have {pendingAssessmentCount} assessment{pendingAssessmentCount > 1 ? 's' : ''} in progress.
              You can resume {pendingAssessmentCount > 1 ? 'one' : 'it'} from the Past Assessments list, or start fresh.
            </p>
            <div className="popup-actions">
              <button
                className="popup-btn"
                onClick={() => {
                  const inProgress = assessmentRecords.find(r => r.status === 'in_process');
                  setShowNewAssessmentConfirm(false);
                  if (inProgress) resumeAssessment(inProgress);
                }}
              >
                Resume Existing
              </button>
              <button
                className="popup-btn"
                style={{ background: '#e2e8f0', color: '#334155' }}
                onClick={proceedNewAssessment}
              >
                Start New
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HealthAssessment;